/**
 * Network Adequacy — CMS MA HSD/NMM-aligned adequacy and compliance oversight.
 * Tabs: Dashboard, Uploads, Findings, Scans, Exception Drafts.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import NAMapView from './NAMapView';
import { TelehealthSpecialtyPicker } from './telehealthSpecialties';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import InfoTooltip from '../../components/InfoTooltip';
import { RosterSection, AttestationsSection, ChangesSection, RosterCorrespondenceSection, RosterSettingsSection, RosterAdequacyCard, ServiceAreaCard, useRosterAccess } from './roster/RosterTabs';
import { AdequacyOverview, FindingsByCounty } from './roster/AdequacyViews';
import { MembersSection, PopulationPointsCard } from './roster/MembersSection';
import { FloridaRunButton, FloridaFindingModal, FloridaScansSection, FloridaWeeklyFile, FloridaSettingsCard, FloridaMapPanel, FloridaWaiverCard, DsnpSettingsCard } from './roster/FloridaViews';
import useAgentProgress from '../../lib/useAgentProgress';
import AgentThinkingPanel from '../../components/AgentThinkingPanel';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, ArcElement);

// Premium chart defaults — Plus Jakarta Sans, refined palette
const MODERN_CHART_DEFAULTS = {
  font: { family: "'Plus Jakarta Sans', system-ui, sans-serif", size: 11 },
  color: 'rgb(100, 116, 139)',
};

const API = process.env.REACT_APP_API_URL || '';
const NA = `${API}/api/network-adequacy`;

// The tab strip follows the platform shell (Precision Privacy, FDR Oversight):
// plain labels, line icons, a tooltip that says what the tab is for.
const TABS = [
  { key: 'dashboard', label: 'Overview', tip: 'Where the network stands: adequacy by county and specialty, what needs attention, and the button that runs adequacy from the roster.', icon: 'M3 12l9-8 9 8M5 10v10h14V10' },
  { key: 'roster', label: 'Provider Roster', tip: 'The plan\'s providers and facilities, one row per location, loaded from any file layout, by API or by drop. Names become CMS codes here. The CMS tables come out of here.', icon: 'M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6' },
  { key: 'attestations', label: 'Office Attestations', tip: 'Every office is asked, on the plan\'s cadence, to confirm or correct its own listing through a personal link. Who answered, who did not, who cannot be reached.', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
  { key: 'members', label: 'Members & Terminations', tip: 'The members on file, and every provider termination: the members placed with the closest provider of the same type, the Excel for Enrollment, and the member notices on the CMS clock.' },
  { key: 'changes', label: 'Changes', tip: 'Corrections from offices and staff, waiting for the department that owns provider data. Accepting writes a new version; nothing is overwritten.', icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' },
  { key: 'findings', label: 'Findings', tip: 'Counties and specialties that fail or run thin, with the recruitment work to close each gap.', icon: 'M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z' },
  { key: 'scans', label: 'Scans & History', tip: 'Every adequacy run, its files, its result, and how the network has moved over time.', icon: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' },
  { key: 'map', label: 'Drive-Time Map', tip: 'Where members can and cannot reach a provider within the CMS time and distance standard.', icon: 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7' },
  { key: 'drafts', label: 'Exception Requests', tip: 'CMS exception requests under 422.116(f), drafted from the findings and exported in the CMS template.', icon: 'M9 12h6m-6 4h6M9 8h6M5 4h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5a1 1 0 011-1z' },
  { key: 'correspondence', label: 'Correspondence', tip: 'Every email this app sent to an office or to the plan, and every reply, downloadable as sent.', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
  { key: 'settings', label: 'Settings', tip: 'CMS reference data, the department that owns provider data, the attestation cadence, API keys.', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z' },
];
// The department that owns provider data sees only these.
const ROSTER_TABS = TABS.filter((t) => ['roster', 'attestations', 'changes', 'correspondence'].includes(t.key));
// Under Florida Medicaid the same tabs carry Florida's words: the exception tab becomes the
// weekly AHCA file, the map and findings say AHCA. Everything else is program-neutral.
const FLORIDA_TAB_WORDS = {
  dashboard: { tip: 'Where the Florida Medicaid network stands under the plan\'s AHCA contract: adequacy by county and standard, what is not met, and the button that runs it from the roster.' },
  findings: { tip: 'Counties and AHCA contract standards that are not met, with the work to close each gap, and the waiver drafts for lines with no available provider.' },
  scans: { tip: 'Every Florida run, its result, the enrollment it used, and how the network has moved over time.' },
  map: { tip: 'Where enrollees can and cannot reach a provider within AHCA\'s time and distance limit.' },
  drafts: { label: 'Weekly AHCA File', tip: 'The provider network file AHCA expects every week, built from the roster: what can go today, what still has to be filled, and AHCA\'s response read back.' },
  settings: { tip: 'The plan\'s AHCA identifiers, its Medicaid enrollment file, and the service area the runs cover.' },
};

function TabTooltip({ text, children }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-flex" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      {children}
      {show && text && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 w-72 px-4 py-3 bg-slate-800 text-white text-xs leading-relaxed rounded-xl shadow-2xl pointer-events-none">
          {text}
          <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2.5 h-2.5 bg-slate-800 rotate-45" />
        </div>
      )}
    </div>
  );
}


function useNAFetch(path, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const refresh = useCallback(() => {
    setLoading(true);
    setError('');
    fetch(`${NA}${path}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => { setError(e.message); setLoading(false); });
  }, [path, ...deps]);
  useEffect(() => { refresh(); }, [refresh]);
  return { data, loading, error, refresh };
}

export default function NetworkAdequacy({ user }) {
  // A department member (not compliance) is here for the roster only.
  const rosterOnly = !!user && user.can_see_privacy !== true;
  const [activeTab, setActiveTab] = useState(rosterOnly ? 'roster' : 'dashboard');
  // Which program the Overview shows: the Medicare Advantage test (CMS, HPMS) or the Florida
  // Medicaid test (AHCA SMMC Table 4). Same roster, two rule sets, one switch.
  const [program, setProgram] = useState(() => { try { return localStorage.getItem('na_overview_program') === 'florida' ? 'florida' : 'medicare'; } catch { return 'medicare'; } });
  const chooseProgram = (p) => { setProgram(p); try { localStorage.setItem('na_overview_program', p); } catch { /* per-browser convenience only */ } };
  const { access: rosterAccess, reload: reloadRosterAccess } = useRosterAccess();
  // Which Florida contract the organization holds: SMMC (Table 4) or D-SNP (Table 2). Set in Settings.
  const flContract = rosterAccess?.settings?.fl_contract_type === 'dsnp' ? 'dsnp' : 'smmc';
  // Bumps when adequacy is run or a finding changes; the Overview and Findings re-read the summary.
  const [adequacyTick, setAdequacyTick] = useState(0);
  const afterAdequacyRun = () => {
    refreshScans(); refreshDashboard(); refreshFindings(); setAdequacyTick((t) => t + 1);
    // The scan runs in the background; look again as it lands.
    [15000, 35000, 70000].forEach((ms) => setTimeout(() => { refreshScans(); refreshDashboard(); refreshFindings(); setAdequacyTick((t) => t + 1); }, ms));
  };
  // A Florida run lands in a few minutes; the Florida screens re-read their summary as it does.
  const afterFloridaRun = () => {
    setAdequacyTick((t) => t + 1);
    [20000, 60000, 120000, 240000, 420000].forEach((ms) => setTimeout(() => setAdequacyTick((t) => t + 1), ms));
  };
  const visibleTabs = rosterOnly ? ROSTER_TABS : program === 'florida' ? TABS.map((t) => (FLORIDA_TAB_WORDS[t.key] ? { ...t, ...FLORIDA_TAB_WORDS[t.key] } : t)) : TABS;
  const [batchLabel, setBatchLabel] = useState('');
  const [atbDeclared, setAtbDeclared] = useState(false);
  const [telehealthSpecialties, setTelehealthSpecialties] = useState([]);
  const [creatingBatch, setCreatingBatch] = useState(false);
  const [selectedFindingId, setSelectedFindingId] = useState(null);
  const [selectedDraftId, setSelectedDraftId] = useState(null);
  const [selectedProviderFileIds, setSelectedProviderFileIds] = useState([]);
  const [selectedFacilityFileIds, setSelectedFacilityFileIds] = useState([]);
  const [scanAuditTrailScanId, setScanAuditTrailScanId] = useState(null);
  const [scanScenarioScanId, setScanScenarioScanId] = useState(null);
  const [recomputingMAR, setRecomputingMAR] = useState(false);
  const [recomputeMARError, setRecomputeMARError] = useState('');
  const [recomputeMARSuccess, setRecomputeMARSuccess] = useState(false);
  // Program the scan will be evaluated against. Defaults to Medicare Advantage so
  // existing users see the same numbers they saw before this feature shipped.
  const [programType, setProgramType] = useState('medicare_advantage');
  const [applicationMode, setApplicationMode] = useState(false);

  const { data: dashboardData, loading: dashLoading, refresh: refreshDashboard } = useNAFetch('/dashboard');
  const { data: batchesData, loading: batchesLoading, refresh: refreshBatches } = useNAFetch('/batches');
  const [findingsSort, setFindingsSort] = useState('members_at_risk_desc');
  const [findingsFilterImpact, setFindingsFilterImpact] = useState('');
  const findingsQs = [];
  if (findingsSort) findingsQs.push('sort=' + encodeURIComponent(findingsSort));
  if (findingsFilterImpact === 'has_impact') findingsQs.push('has_member_impact=1');
  const findingsPath = '/findings' + (findingsQs.length ? '?' + findingsQs.join('&') : '');
  const { data: findingsData, loading: findingsLoading, refresh: refreshFindings } = useNAFetch(findingsPath, [findingsSort, findingsFilterImpact]);
  const { data: scansData, loading: scansLoading, refresh: refreshScans } = useNAFetch('/scans');
  const { data: draftsData, loading: draftsLoading, refresh: refreshDrafts } = useNAFetch('/exception-drafts');
  const { data: readinessData, refresh: refreshReadiness } = useNAFetch('/scan-readiness');
  const { data: settingsData, loading: settingsLoading, refresh: refreshSettings } = useNAFetch('/settings/cms-minimums');

  const selectionInitialized = useRef(false);
  const prevFileCount = useRef(0);
  useEffect(() => {
    if (!readinessData) return;
    const totalFiles = (readinessData.provider_files?.length ?? 0) + (readinessData.facility_files?.length ?? 0);
    if (totalFiles > prevFileCount.current) {
      selectionInitialized.current = false;
      prevFileCount.current = totalFiles;
    }
    if (selectionInitialized.current) return;
    if (totalFiles === 0) return;
    selectionInitialized.current = true;
    if (readinessData.provider_files?.length) setSelectedProviderFileIds(readinessData.provider_files.map((f) => f.id));
    if (readinessData.facility_files?.length) setSelectedFacilityFileIds(readinessData.facility_files.map((f) => f.id));
  }, [readinessData]);

  const createBatch = () => {
    if (creatingBatch) return;
    setCreatingBatch(true);
    fetch(`${NA}/batches`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label: batchLabel || null, atb_declared: atbDeclared, telehealth_specialties: atbDeclared ? telehealthSpecialties : [] })
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) { setBatchLabel(''); setAtbDeclared(false); refreshBatches(); refreshReadiness(); }
        setCreatingBatch(false);
      })
      .catch(() => setCreatingBatch(false));
  };

  const [lastScanNote, setLastScanNote] = useState(null);
  const [lastScanReconciliation, setLastScanReconciliation] = useState(null);
  const runScan = () => {
    setLastScanNote(null);
    setLastScanReconciliation(null);
    fetch(`${NA}/scans/run`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ provider_file_ids: selectedProviderFileIds, facility_file_ids: selectedFacilityFileIds, program_type: programType, application_mode: applicationMode })
    })
      .then((r) => r.json())
      .then((d) => {
        if (!d.success) return;
        if (d.status === 'running' && d.scan_id) {
          // Background scan: poll the scans list until it completes, then refresh everything.
          setLastScanNote(d.note || 'Scan running in the background…');
          refreshScans();
          let polls = 0;
          const timer = setInterval(() => {
            polls += 1;
            fetch(`${NA}/scans`, { credentials: 'include' })
              .then((r) => r.json())
              .then((sd) => {
                const scan = (sd.scans || []).find((s) => s.id === d.scan_id);
                const done = scan && scan.status !== 'running';
                if (done || polls > 360) {
                  clearInterval(timer);
                  setLastScanNote(scan?.status === 'failed' ? 'Scan failed — open the scan for details.' : null);
                  refreshScans();
                  refreshDashboard();
                  refreshFindings();
                }
              })
              .catch(() => { if (polls > 360) clearInterval(timer); });
          }, 5000);
          return;
        }
        refreshScans();
        refreshDashboard();
        refreshFindings();
        const fc = (d.findings_created ?? 0) + (d.findings_updated ?? 0);
        const fail = d.fail_count ?? 0;
        if (d.reconciliation) setLastScanReconciliation(d.reconciliation);
        if (d.note) setLastScanNote(d.note);
        if (fc === 0 && fail === 0) {
          console.warn('[NA runScan response] 0 deficiencies. Check: (1) Files selected? (2) HSD reference ingested? (3) GET /scans/debug for diagnostics.');
        }
      });
  };

  return (
    <div className="h-full flex flex-col bg-[#f8fafc] min-w-0 min-h-0 text-slate-800">
      <header className="flex-shrink-0 bg-white border-b border-slate-200/60">
        <div className="px-8 pt-6 pb-4">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-orange-500/25 ring-2 ring-white/20">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl font-bold text-slate-800 tracking-tight">Network Adequacy</h1>
              <p className="text-slate-400 text-sm mt-0.5">{rosterOnly ? 'The plan\'s provider roster: what the offices confirm, what changes wait for your decision, and every email on the record.' : program === 'florida' ? (flContract === 'dsnp' ? 'The plan\'s provider network under its AHCA D-SNP contract: the Medicare standard as the base, the contract\'s behavioral health lines measured county by county, gaps worked, the weekly provider network file out.' : 'The plan\'s provider network against AHCA\'s SMMC standards: the same roster in, Florida adequacy measured county by county, gaps worked, the weekly provider network file out.') : 'The plan\'s provider network against the CMS standards: the roster in, adequacy measured, gaps and exceptions worked, the HPMS tables out.'}</p>
            </div>
            {!rosterOnly && (
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 text-sm font-semibold shadow-sm" role="tablist" aria-label="Program">
                  <button type="button" onClick={() => { chooseProgram('medicare'); setSelectedFindingId(null); }} className={`rounded-lg px-4 py-1.5 transition ${program === 'medicare' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>Medicare Advantage</button>
                  <button type="button" onClick={() => { chooseProgram('florida'); setSelectedFindingId(null); }} className={`rounded-lg px-4 py-1.5 transition ${program === 'florida' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>{flContract === 'dsnp' ? 'Florida D-SNP' : 'Florida SMMC'}</button>
                </div>
                <div className="text-[11px] text-slate-400">{program === 'medicare' ? 'CMS standards (42 CFR 422.116), measured as HPMS does' : flContract === 'dsnp' ? 'AHCA D-SNP contract, Attachment II-B Table 2, on the Medicare standard the contract points to' : 'AHCA SMMC contract, Exhibit II-A Table 4, measured for enrollees in each county'}</div>
              </div>
            )}
          </div>
        </div>
        <div className="px-8 pb-0">
          <div className="flex gap-0.5 flex-wrap">
            {visibleTabs.map((tab) => (
              <TabTooltip key={tab.key} text={tab.tip}>
                <button type="button" onClick={() => { setActiveTab(tab.key); setSelectedFindingId(null); if (tab.key !== 'drafts') setSelectedDraftId(null); }}
                  className={`relative flex items-center gap-1.5 px-4 py-3 text-sm font-semibold transition-all rounded-t-xl whitespace-nowrap ${
                    activeTab === tab.key ? 'bg-[#f8fafc] text-orange-700 border border-slate-200/60 border-b-[#f8fafc] -mb-px z-10' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}>
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d={tab.icon} /></svg>
                  {tab.label}
                </button>
              </TabTooltip>
            ))}
          </div>
        </div>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="px-8 py-6 max-w-[1500px]">
        {activeTab === 'dashboard' && program === 'florida' && (
          <div className="mb-4">
            <AdequacyOverview
              program="florida"
              refreshKey={adequacyTick}
              runButton={<FloridaRunButton onRan={afterFloridaRun} />}
              onOpenFinding={(id) => setSelectedFindingId(id)}
              onGoToFindings={() => setActiveTab('findings')}
            />
          </div>
        )}
        {program === 'florida' && selectedFindingId && (activeTab === 'dashboard' || activeTab === 'findings') && (
          <FloridaFindingModal findingId={selectedFindingId} onClose={() => setSelectedFindingId(null)} onUpdated={() => setAdequacyTick((t) => t + 1)} />
        )}
        {activeTab === 'dashboard' && program === 'florida' && (
          <details className="mb-4 bg-white rounded-2xl border border-slate-200">
            <summary className="cursor-pointer select-none px-5 py-3 text-sm font-semibold text-slate-700">How Florida adequacy is measured</summary>
            <div className="px-5 pb-4 text-slate-600 text-sm leading-relaxed space-y-2">
              <p><strong>The rule.</strong> AHCA's SMMC contract (Attachment II, Exhibit II-A, Table 4) sets, for each provider type, a maximum travel time and distance for enrollees in urban counties and in rural counties, a minimum count per county for some types, and a provider-to-enrollee ratio for others. The contract names no lower share, so every enrollee location in the county has to be within the limit for the standard to be met; the share within reach is shown either way.</p>
              <p><strong>Where enrollees are.</strong> Each ZIP in a service-area county is a starting point, weighted by the plan's Medicaid enrollment file when one is loaded in Settings, otherwise by Census population. Providers come from the same roster the Medicare side uses, placed by the same geocoding and measured over the same road network.</p>
              <p><strong>What the words mean.</strong> <em>Met</em> and <em>Not met</em> are measured results. <em>No source in roster</em> means no roster row stands for that AHCA category yet, so nothing was measured. <em>Needs enrollment</em> means the standard is a ratio and waits for the enrollment file.</p>
            </div>
          </details>
        )}
        {activeTab === 'dashboard' && program === 'medicare' && (
          <div className="mb-4">
            <AdequacyOverview
              refreshKey={adequacyTick}
              runButton={<RosterAdequacyCard compact onRan={afterAdequacyRun} />}
              onOpenFinding={(id) => setSelectedFindingId(id)}
              onGoToFindings={() => setActiveTab('findings')}
            />
          </div>
        )}
        {activeTab === 'dashboard' && program === 'medicare' && selectedFindingId && (
          <FindingDetailModal findingId={selectedFindingId} apiBase={NA} onClose={() => setSelectedFindingId(null)} onUpdated={() => { refreshFindings(); setAdequacyTick((t) => t + 1); }} onSwitchToDrafts={(draftId) => { refreshDrafts(); setActiveTab('drafts'); setSelectedDraftId(draftId); }} />
        )}
        {activeTab === 'dashboard' && program === 'medicare' && (
          <details className="mb-4 bg-white rounded-2xl border border-slate-200">
            <summary className="cursor-pointer select-none px-5 py-3 text-sm font-semibold text-slate-700">How adequacy is evaluated</summary>
            <div className="px-5 pb-4">
  <h2 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-2">
    <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
    How we evaluate
  </h2>
  <p className="text-slate-600 text-sm leading-relaxed">
    <strong>(1) Count</strong> <InfoTooltip text="We count unique providers or facilities in each county and specialty." />: Do you have enough providers in each county? We compare your network to the minimum counts for that county type under the regulatory program you pick (Medicare Advantage — <strong>42 CFR 422.116</strong>; Medicaid — <strong>42 CFR 438.68</strong>; ACA Marketplace — <strong>45 CFR 156.230</strong>).
  </p>
  <p className="text-slate-600 text-sm leading-relaxed mt-2">
    <strong>(2) Drive time & distance</strong> <InfoTooltip text="We measure how far members must drive to reach the nearest provider. We sample locations (ZIP centers, Census points) and check whether each can reach a provider within the program's limits (e.g. 30 min or 15 mi for PCP in a metro county)." />: Can members reach the nearest provider within the allowed drive time? At least <strong>90%</strong> <InfoTooltip text="Metro and large metro: 90% of member locations must be within standard. Federal default for Medicare Advantage and ACA Marketplace." /> (metro) or <strong>85%</strong> <InfoTooltip text="Micro, rural, CEAC: 85% of member locations must be within standard. The threshold for Medicaid in CEAC counties can be lower." /> (rural) of locations must be within standard. A county fails if it lacks the required count or if too few member locations meet the drive-time standard.
  </p>
  <p className="text-slate-600 text-sm leading-relaxed mt-2">
    <strong>(3) Data confidence & ghost detection</strong> <InfoTooltip text="We score every provider 0–100 on data completeness, NPI validity, and corroboration across uploads. Records below 50 are flagged as ghost candidates and surface in the dashboard for review." />: We score every provider and facility for completeness, valid NPI, and corroboration across uploads. Low-confidence rows are flagged as <em>ghost candidates</em>.
  </p>
  <p className="text-slate-500 text-xs mt-2">
    <strong>Deficient</strong> <InfoTooltip text="Does not meet the program's network adequacy requirement. Below the required minimum or below the drive-time threshold." /> = below the required minimum. <strong>Fragile</strong> <InfoTooltip text="Meets the minimum but close to the threshold. Consider adding capacity to avoid falling out of compliance." /> = meets the minimum but only by a small margin.
  </p>

            </div>
          </details>
        )}
        {activeTab === 'dashboard' && program === 'medicare' && (
          <details className="mb-4 bg-white rounded-2xl border border-slate-200">
            <summary className="cursor-pointer select-none px-5 py-3 text-sm font-semibold text-slate-700">More detail: coverage, directory accuracy, members at risk, charts</summary>
            <div className="px-5 pb-5">
          <DashboardSection
            data={dashboardData}
            loading={dashLoading}
            onRefresh={refreshDashboard}
            readiness={readinessData}
            onRunScan={runScan}
            lastScanNote={lastScanNote}
            lastScanReconciliation={lastScanReconciliation}
            selectedProviderFileIds={selectedProviderFileIds}
            selectedFacilityFileIds={selectedFacilityFileIds}
            onSelectProviderFiles={setSelectedProviderFileIds}
            onSelectFacilityFiles={setSelectedFacilityFileIds}
            programType={programType}
            onSelectProgramType={setProgramType}
            applicationMode={applicationMode}
            onToggleApplicationMode={setApplicationMode}
            apiBase={NA}
            onSwitchToFindings={(opts) => { setActiveTab('findings'); if (opts?.hasMemberImpact) setFindingsFilterImpact('has_impact'); }}
            onRecomputeMembersAtRisk={async () => {
              const scanId = dashboardData?.latest_scan_id ?? dashboardData?.latest_scan?.id;
              setRecomputeMARError('');
              setRecomputeMARSuccess(false);
              if (!scanId) {
                setRecomputeMARError('No scan found. Run a scan first.');
                return;
              }
              setRecomputingMAR(true);
              try {
                const r = await fetch(`${NA}/scans/${scanId}/compute-members-at-risk`, { method: 'POST', credentials: 'include' });
                let d;
                try { d = await r.json(); } catch { d = {}; }
                if (!r.ok) d = { ...d, success: false, message: d.message || `Request failed (${r.status})` };
                if (d.success) {
                  refreshDashboard();
                  refreshFindings();
                  setRecomputeMARSuccess(true);
                  setTimeout(() => setRecomputeMARSuccess(false), 3000);
                } else {
                  setRecomputeMARError(d.message || 'Recompute failed');
                }
              } catch (e) {
                setRecomputeMARError(e?.message || 'Recompute failed');
                console.warn('Recompute failed:', e);
              } finally {
                setRecomputingMAR(false);
              }
            }}
            recomputingMAR={recomputingMAR}
            recomputeMARError={recomputeMARError}
            recomputeMARSuccess={recomputeMARSuccess}
          />
            </div>
          </details>
        )}
        {activeTab === 'findings' && program === 'florida' && (
          <div className="space-y-4">
            <FindingsByCounty program="florida" refreshKey={adequacyTick} onOpenFinding={(id) => setSelectedFindingId(id)} />
            {flContract === 'dsnp' && <FloridaWaiverCard refreshKey={adequacyTick} canDraft={rosterAccess?.level === 'compliance'} />}
          </div>
        )}
        {activeTab === 'findings' && program === 'medicare' && (
          <>
            <FindingsByCounty refreshKey={adequacyTick} onOpenFinding={(id) => setSelectedFindingId(id)} />
            {selectedFindingId && (
              <FindingDetailModal findingId={selectedFindingId} apiBase={NA} onClose={() => setSelectedFindingId(null)} onUpdated={() => { refreshFindings(); setAdequacyTick((t) => t + 1); }} onSwitchToDrafts={(draftId) => { refreshDrafts(); setActiveTab('drafts'); setSelectedDraftId(draftId); }} />
            )}
          </>
        )}
        {activeTab === 'scans' && program === 'florida' && (
          <FloridaScansSection refreshKey={adequacyTick} onRan={afterFloridaRun} />
        )}
        {activeTab === 'scans' && program === 'medicare' && (
          <ScansSection
            apiBase={NA}
            scans={scansData?.scans || []}
            loading={scansLoading}
            onRefresh={refreshScans}
            readiness={readinessData}
            onRunScan={runScan}
            lastScanNote={lastScanNote}
            lastScanReconciliation={lastScanReconciliation}
            selectedProviderFileIds={selectedProviderFileIds}
            selectedFacilityFileIds={selectedFacilityFileIds}
            onSelectProviderFiles={setSelectedProviderFileIds}
            onSelectFacilityFiles={setSelectedFacilityFileIds}
            onShowScanAuditTrail={setScanAuditTrailScanId}
            onShowScanScenario={setScanScenarioScanId}
            programType={programType}
            onSelectProgramType={setProgramType}
          />
        )}
        {scanScenarioScanId && (
          <ScenarioModal
            scanId={scanScenarioScanId}
            apiBase={NA}
            onClose={() => setScanScenarioScanId(null)}
          />
        )}
        {scanAuditTrailScanId && (
          <ScanAuditTrailModal
            scanId={scanAuditTrailScanId}
            apiBase={NA}
            onClose={() => setScanAuditTrailScanId(null)}
          />
        )}
        {activeTab === 'map' && program === 'florida' && (
          <FloridaMapPanel onGoToOverview={() => setActiveTab('dashboard')} />
        )}
        {activeTab === 'map' && program === 'medicare' && (
          <NAMapView
            apiBase={NA}
            scans={scansData?.scans || []}
            loadingScans={scansLoading}
            onRefreshScans={refreshScans}
          />
        )}
        {activeTab === 'drafts' && program === 'florida' && (
          <FloridaWeeklyFile canRun={rosterAccess?.level === 'compliance'} />
        )}
        {activeTab === 'drafts' && program === 'medicare' && (
          <DraftsSection
            drafts={draftsData?.drafts || []}
            loading={draftsLoading}
            onRefresh={refreshDrafts}
            apiBase={NA}
            selectedDraftId={selectedDraftId}
            onSelectDraft={setSelectedDraftId}
          />
        )}
        {activeTab === 'settings' && program === 'florida' && (
          <div className="space-y-4">
            <FloridaSettingsCard settings={rosterAccess?.settings} canWrite={rosterAccess?.level === 'compliance'} onSaved={reloadRosterAccess} />
            {flContract === 'dsnp' && <DsnpSettingsCard canWrite={rosterAccess?.level === 'compliance'} refreshKey={adequacyTick} />}
            <ServiceAreaCard />
            <p className="text-[12px] text-slate-500">The department that owns provider data, the attestation cadence and the API keys are shared by both programs and are set under Medicare Advantage.</p>
          </div>
        )}
        {activeTab === 'settings' && program === 'medicare' && (
          <>
            <div className="mb-4"><PopulationPointsCard canWrite={rosterAccess?.level === 'compliance'} /></div>
            <SettingsSection
              data={settingsData}
              loading={settingsLoading}
              onRefresh={refreshSettings}
              apiBase={NA}
              populationWeightedZipCount={readinessData?.population_weighted_zip_count ?? null}
              onRefreshReadiness={refreshReadiness}
            />
            {rosterAccess?.level === 'compliance' && (
              <div className="mt-6">
                <h2 className="text-sm font-semibold text-slate-800 mb-3">Provider roster</h2>
                <RosterSettingsSection access={rosterAccess} onSaved={reloadRosterAccess} />
              </div>
            )}
          </>
        )}
        {activeTab === 'roster' && (
          <>
            <RosterSection access={rosterAccess} onChanged={reloadRosterAccess} />
            {!rosterOnly && (
              <details className="mt-6 bg-white rounded-2xl border border-slate-200">
                <summary className="cursor-pointer select-none px-5 py-3 text-sm font-semibold text-slate-700">Load CMS-format files directly</summary>
                <div className="px-5 pb-5"><p className="text-[12.5px] text-slate-500 mb-3">For files already in the exact HPMS provider and facility table layout. Everything else goes through the roster above, which builds these tables for you.</p>
          <UploadsSection
            batches={batchesData?.batches || []}
            loading={batchesLoading}
            onRefresh={refreshBatches}
            onUploadSuccess={refreshReadiness}
            batchLabel={batchLabel}
            setBatchLabel={setBatchLabel}
            atbDeclared={atbDeclared}
            setAtbDeclared={setAtbDeclared}
            telehealthSpecialties={telehealthSpecialties}
            setTelehealthSpecialties={setTelehealthSpecialties}
            createBatch={createBatch}
            creatingBatch={creatingBatch}
          />

                </div>
              </details>
            )}
          </>
        )}
        {activeTab === 'attestations' && <AttestationsSection access={rosterAccess} />}
        {activeTab === 'changes' && <ChangesSection access={rosterAccess} />}
        {activeTab === 'members' && <MembersSection access={rosterAccess} />}
        {activeTab === 'correspondence' && <RosterCorrespondenceSection />}
      </div>
      </div>
    </div>
  );
}

// Clean county display: "Broward, FL" — avoid "FL, FL" redundancy; code in tooltip
function formatCountyDisplay(ssaStateCountyCode, countyName, stateName) {
  if (!ssaStateCountyCode) return '';
  const code = String(ssaStateCountyCode).trim();
  let name = countyName && countyName !== code ? String(countyName).trim() : null;
  const state = stateName ? String(stateName).trim() : null;
  if (name && state) {
    if (name.endsWith(', ' + state) || name.endsWith(',' + state)) return name;
    return `${name}, ${state}`;
  }
  if (name) return name;
  return `County ${code}`;
}

const CHART_COLORS = {
  deficient: 'rgba(239, 68, 68, 0.9)',
  fragile: 'rgba(245, 158, 11, 0.9)',
  other: 'rgba(148, 163, 184, 0.7)',
  border: 'rgba(255, 255, 255, 1)',
  grid: 'rgba(226, 232, 240, 0.8)',
};

const PROGRAM_LABELS = {
  medicare_advantage: 'Medicare Advantage',
  medicaid: 'Medicaid Managed Care',
  aca_marketplace: 'ACA Marketplace'
};
const PROGRAM_SOURCES = {
  medicare_advantage: '42 CFR 422.116',
  medicaid: '42 CFR 438.68',
  aca_marketplace: '45 CFR 156.230'
};

function formatPct(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  return `${n.toFixed(n >= 100 ? 0 : 1)}%`;
}

function pctColor(v) {
  if (v == null) return 'text-slate-400';
  const n = Number(v);
  if (n >= 90) return 'text-emerald-600';
  if (n >= 80) return 'text-amber-600';
  return 'text-red-600';
}

function AdequacyHeadline({ headline, programType, onSelectProgramType, applicationMode = false, onToggleApplicationMode, apiBase, onRefreshDashboard }) {
  const [showGhosts, setShowGhosts] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [recomputeResult, setRecomputeResult] = useState(null);
  const [recomputeError, setRecomputeError] = useState('');
  const overall = headline?.overall_pct;
  const directoryAccuracy = headline?.directory_accuracy_pct;
  const ghostCount = headline?.ghost_provider_count ?? null;
  const providerTotal = headline?.provider_total_count ?? null;
  const lastProgram = headline?.program_type || 'medicare_advantage';

  const headlineMissing = headline == null || overall == null;

  async function handleRecompute() {
    if (!headline?.scan_id) return;
    setRecomputing(true);
    setRecomputeError('');
    setRecomputeResult(null);
    try {
      const r = await fetch(`${apiBase}/scans/${headline.scan_id}/recompute-confidence`, {
        method: 'POST',
        credentials: 'include'
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setRecomputeError(d.message || `Request failed (${r.status})`);
      } else {
        setRecomputeResult(d);
        if (onRefreshDashboard) onRefreshDashboard();
      }
    } catch (e) {
      setRecomputeError(e?.message || 'Recompute failed');
    } finally {
      setRecomputing(false);
    }
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 animate-slide-up">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Network adequacy at a glance</h2>
          <p className="text-slate-500 text-sm mt-0.5">
            {headlineMissing
              ? 'Run a scan to see your overall coverage.'
              : `Latest scan evaluated against ${PROGRAM_LABELS[lastProgram] || lastProgram} (${PROGRAM_SOURCES[lastProgram] || ''}).`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs font-medium text-slate-600 mr-1">Next scan as:</label>
          <select
            value={programType}
            onChange={(e) => onSelectProgramType?.(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
            <option value="medicare_advantage">Medicare Advantage</option>
            <option value="medicaid">Medicaid Managed Care</option>
            <option value="aca_marketplace">ACA Marketplace</option>
          </select>
          <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600 cursor-pointer" title="Application review mode (42 CFR 422.116(d)(7)): applies the automatic +10 point applicant credit to every specialty and counts Letter of Intent rows. Use for initial/service-area-expansion applications; leave off for operational (triennial) reviews.">
            <input
              type="checkbox"
              checked={applicationMode}
              onChange={(e) => onToggleApplicationMode?.(e.target.checked)}
              className="rounded border-slate-300"
            />
            Application mode (+10 credit, LOIs count)
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <HeadlineCard
          label="Overall coverage"
          value={overall}
          subtitle="Counties × specialties passing"
          tooltipTitle="How overall coverage is calculated"
          tooltipNode={(
            <>
              Every required (county × specialty) is evaluated against the regulatory program you picked (Medicare Advantage 42 CFR 422.116, Medicaid 42 CFR 438.68, or ACA Marketplace 45 CFR 156.230). A cell <strong>passes</strong> when:
              <ul className="mt-1.5 list-disc pl-4 space-y-0.5">
                <li>You have at least the <strong>minimum number</strong> of providers/facilities for that county type, <em>and</em></li>
                <li>The required share of members can reach one within the <strong>drive-time / distance</strong> standard (90% in metro counties, 85% in rural)</li>
              </ul>
              <span className="block mt-1.5"><strong>Overall coverage</strong> is the share of evaluated cells that pass. This is the headline number industry tools (Quest, Zelis, Optum) report.</span>
            </>
          )}
        />
        <HeadlineCard
          label="Directory accuracy"
          value={directoryAccuracy}
          subtitle={providerTotal != null && ghostCount != null
            ? `${ghostCount.toLocaleString()} of ${providerTotal.toLocaleString()} flagged as ghosts`
            : 'Score every provider record for completeness, valid NPI, and corroboration'}
          tooltipTitle="How directory accuracy is scored"
          tooltipNode={(
            <>
              We score every provider and facility record 0&ndash;100 on five things, then report the share scoring 70 or higher:
              <ul className="mt-1.5 list-disc pl-4 space-y-0.5">
                <li><strong>Field completeness (40 pts):</strong> NPI, name, street address, city, state, ZIP</li>
                <li><strong>Address quality (20 pts):</strong> looks like a real street, not a PO box, valid ZIP format</li>
                <li><strong>NPI validity (15 pts):</strong> passes the official CMS Luhn checksum &mdash; catches typos and fakes</li>
                <li><strong>Corroboration (15 pts):</strong> same NPI seen in two or more separate uploads</li>
                <li><strong>Recency (10 pts):</strong> record loaded within the last 12 months</li>
              </ul>
              <span className="block mt-1.5">Records under <strong>50</strong> are flagged as <em>ghost candidates</em> and surface in the "View ghosts" review &mdash; this aligns with NCQA 2026 ghost-provider detection.</span>
            </>
          )}
          rightSlot={
            <div className="flex flex-col items-end gap-1">
              {headline?.scan_id && (
                <button
                  type="button"
                  onClick={handleRecompute}
                  disabled={recomputing}
                  className="text-xs font-medium text-emerald-700 hover:text-emerald-800 hover:underline disabled:opacity-50"
                  title="Score every provider and facility record now and show the result"
                >
                  {recomputing ? 'Computing…' : 'Recompute'}
                </button>
              )}
              {ghostCount > 0 && headline?.scan_id && (
                <button
                  type="button"
                  onClick={() => setShowGhosts(true)}
                  className="text-xs font-medium text-violet-600 hover:text-violet-700 hover:underline"
                >
                  View ghosts →
                </button>
              )}
            </div>
          }
        />
      </div>

      {(recomputeResult || recomputeError) && (
        <div className={`mt-4 p-3 rounded-lg text-xs ${recomputeError ? 'bg-red-50 border border-red-200 text-red-800' : 'bg-emerald-50 border border-emerald-200 text-emerald-900'}`}>
          {recomputeError ? (
            <span>Recompute failed: {recomputeError}</span>
          ) : (
            <span>
              Scored <strong>{recomputeResult.total_records.toLocaleString()}</strong> records
              ({recomputeResult.provider_total.toLocaleString()} providers, {recomputeResult.facility_total.toLocaleString()} facilities)
              in {(recomputeResult.elapsed_ms / 1000).toFixed(1)}s.
              {' '}<strong>{recomputeResult.high_confidence.toLocaleString()}</strong> high-confidence,
              {' '}<strong>{recomputeResult.ghosts.toLocaleString()}</strong> ghost candidate(s).
              {' '}Directory accuracy: <strong>{recomputeResult.directory_accuracy_pct ?? '—'}%</strong>.
              {recomputeResult.total_records === 0 && (
                <span className="block mt-1">No normalized provider or facility records found for this org. Upload provider/facility files in the Uploads tab.</span>
              )}
            </span>
          )}
        </div>
      )}

      {headlineMissing && (
        <p className="mt-4 text-xs text-slate-500">
          Tip: select the program above, then click <strong>Run scan</strong>. We'll evaluate every county/specialty
          against the rules for that program (Medicare Advantage, Medicaid, or ACA Marketplace).
        </p>
      )}

      {showGhosts && headline?.scan_id && (
        <GhostCandidatesModal
          apiBase={apiBase}
          scanId={headline.scan_id}
          onClose={() => setShowGhosts(false)}
        />
      )}
    </div>
  );
}

function HeadlineCard({ label, value, subtitle, tooltip, tooltipNode, tooltipTitle, rightSlot }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50/40 p-4">
      <div className="flex items-start justify-between">
        <p className="text-slate-500 text-xs font-medium uppercase tracking-wider flex items-center gap-1">
          {label}
          {tooltipNode
            ? <InfoTooltip title={tooltipTitle} content={tooltipNode} width={380} />
            : tooltip
              ? <InfoTooltip text={tooltip} />
              : null}
        </p>
        {rightSlot}
      </div>
      <p className={`text-4xl font-bold tracking-tight tabular-nums mt-2 ${pctColor(value)}`}>
        {formatPct(value)}
      </p>
      <p className="text-slate-500 text-xs mt-1">{subtitle}</p>
    </div>
  );
}

function GhostCandidatesModal({ apiBase, scanId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [providers, setProviders] = useState([]);
  const [facilities, setFacilities] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${apiBase}/scans/${scanId}/ghost-candidates?limit=100`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        if (d.success) {
          setProviders(d.providers || []);
          setFacilities(d.facilities || []);
        } else {
          setError(d.message || 'Could not load ghost candidates.');
        }
        setLoading(false);
      })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [apiBase, scanId]);

  const renderFlag = (f) => f.replace(/_/g, ' ');

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Ghost candidate review</h3>
            <p className="text-slate-500 text-xs mt-0.5">Records with data confidence below 50. Review and remove or correct.</p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-6">
          {loading && <p className="text-slate-500 text-sm">Loading…</p>}
          {error && <p className="text-red-600 text-sm">{error}</p>}
          {!loading && !error && providers.length === 0 && facilities.length === 0 && (
            <p className="text-slate-500 text-sm">No ghost candidates found in this scan. Nicely clean directory.</p>
          )}
          {providers.length > 0 && (
            <div>
              <h4 className="font-semibold text-slate-800 text-sm mb-2">Providers ({providers.length})</h4>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3 font-medium">NPI</th>
                    <th className="py-2 pr-3 font-medium">Name</th>
                    <th className="py-2 pr-3 font-medium">Address</th>
                    <th className="py-2 pr-3 font-medium text-right">Score</th>
                    <th className="py-2 font-medium">Reasons</th>
                  </tr>
                </thead>
                <tbody>
                  {providers.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100 last:border-0 align-top">
                      <td className="py-2 pr-3 font-mono text-slate-700">{p.npi || '—'}</td>
                      <td className="py-2 pr-3 text-slate-800">{p.provider_name || '—'}</td>
                      <td className="py-2 pr-3 text-slate-600">{[p.street_address, p.city, p.state, p.zip_code].filter(Boolean).join(', ') || '—'}</td>
                      <td className="py-2 pr-3 text-right font-semibold text-slate-700">{p.data_confidence_score ?? '—'}</td>
                      <td className="py-2 text-slate-500">{Array.isArray(p.data_confidence_flags) ? p.data_confidence_flags.map(renderFlag).join(', ') : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {facilities.length > 0 && (
            <div>
              <h4 className="font-semibold text-slate-800 text-sm mb-2">Facilities ({facilities.length})</h4>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3 font-medium">NPI</th>
                    <th className="py-2 pr-3 font-medium">Name</th>
                    <th className="py-2 pr-3 font-medium">Address</th>
                    <th className="py-2 pr-3 font-medium text-right">Score</th>
                    <th className="py-2 font-medium">Reasons</th>
                  </tr>
                </thead>
                <tbody>
                  {facilities.map((f) => (
                    <tr key={f.id} className="border-b border-slate-100 last:border-0 align-top">
                      <td className="py-2 pr-3 font-mono text-slate-700">{f.npi || '—'}</td>
                      <td className="py-2 pr-3 text-slate-800">{f.facility_name || '—'}</td>
                      <td className="py-2 pr-3 text-slate-600">{[f.street_address, f.city, f.state, f.zip_code].filter(Boolean).join(', ') || '—'}</td>
                      <td className="py-2 pr-3 text-right font-semibold text-slate-700">{f.data_confidence_score ?? '—'}</td>
                      <td className="py-2 text-slate-500">{Array.isArray(f.data_confidence_flags) ? f.data_confidence_flags.map(renderFlag).join(', ') : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DashboardSection({ data, loading, onRefresh, readiness, onRunScan, lastScanNote, lastScanReconciliation, selectedProviderFileIds, selectedFacilityFileIds, onSelectProviderFiles, onSelectFacilityFiles, onSwitchToFindings, onRecomputeMembersAtRisk, recomputingMAR = false, recomputeMARError = '', recomputeMARSuccess = false, programType = 'medicare_advantage', onSelectProgramType, applicationMode = false, onToggleApplicationMode, apiBase }) {
  if (loading) return <div className="text-slate-500">Loading dashboard…</div>;
  const active = data?.active_findings ?? 0;
  const dbg = data?._debug_dashboard ?? data?.debug_dashboard;
  const deficient = data?.deficient_count ?? 0;
  const fragile = data?.fragile_count ?? 0;
  const membersAtRiskTotal = data?.members_at_risk_total ?? 0;
  const topGapsByMembers = data?.top_gaps_by_members ?? [];
  const hasMembership = data?.has_membership ?? false;
  const latestScanId = data?.latest_scan_id ?? data?.latest_scan?.id;
  const adequacyHeadline = data?.adequacy_headline ?? null;
  const scanReady = readiness?.ready ?? false;
  const charts = data?.charts ?? {};
  const byCounty = charts.by_county ?? [];
  const bySpecialty = charts.by_specialty ?? [];
  const statusBreakdown = charts.status_breakdown ?? { deficient: 0, fragile: 0, total: 0 };
  const trend = data?.trend ?? [];
  const byLob = data?.by_lob ?? [];

  const hasCharts = byCounty.length > 0 || bySpecialty.length > 0 || statusBreakdown.total > 0;

  const countyLabels = byCounty.map((c) => formatCountyDisplay(c.ssa_state_county_code, c.county_name, c.state_name));
  const countyBarData = {
    labels: countyLabels,
    datasets: [
      { label: 'CMS-deficient', data: byCounty.map((c) => c.deficient_count), backgroundColor: CHART_COLORS.deficient, borderColor: CHART_COLORS.border, borderWidth: 0, borderRadius: { topLeft: 6, topRight: 6 }, barPercentage: 0.72, categoryPercentage: 0.85 },
      { label: 'Fragile', data: byCounty.map((c) => c.fragile_count), backgroundColor: CHART_COLORS.fragile, borderColor: CHART_COLORS.border, borderWidth: 0, borderRadius: { topLeft: 6, topRight: 6 }, barPercentage: 0.72, categoryPercentage: 0.85 },
    ],
  };

  const specialtyLabels = bySpecialty.map((s) => {
    const name = (s.specialty_name || s.specialty_code || '').trim();
    const rt = s.result_type === 'facility' ? 'facility' : 'provider';
    return name.length > 28 ? `${name.slice(0, 25)}… (${rt})` : `${name} (${rt})`;
  });
  const specialtyBarData = {
    labels: specialtyLabels,
    datasets: [
      { label: 'CMS-deficient', data: bySpecialty.map((s) => s.deficient_count), backgroundColor: CHART_COLORS.deficient, borderColor: CHART_COLORS.border, borderWidth: 0, borderRadius: { topLeft: 6, topRight: 6 }, barPercentage: 0.72, categoryPercentage: 0.85 },
      { label: 'Fragile', data: bySpecialty.map((s) => s.fragile_count), backgroundColor: CHART_COLORS.fragile, borderColor: CHART_COLORS.border, borderWidth: 0, borderRadius: { topLeft: 6, topRight: 6 }, barPercentage: 0.72, categoryPercentage: 0.85 },
    ],
  };

  const otherActive = Math.max(0, statusBreakdown.total - statusBreakdown.deficient - statusBreakdown.fragile);
  const donutData = {
    labels: ['CMS-deficient', 'Fragile', 'Other active'],
    datasets: [{
      data: [statusBreakdown.deficient, statusBreakdown.fragile, otherActive],
      backgroundColor: [CHART_COLORS.deficient, CHART_COLORS.fragile, CHART_COLORS.other],
      borderColor: 'transparent',
      borderWidth: 0,
      hoverOffset: 8,
      spacing: 2,
    }],
  };

  const modernChartPlugins = {
    legend: {
      position: 'top',
      align: 'end',
      labels: {
        usePointStyle: true,
        pointStyle: 'circle',
        padding: 16,
        font: { ...MODERN_CHART_DEFAULTS.font, size: 12, weight: '500' },
        color: '#475569',
      },
    },
    tooltip: {
      enabled: true,
      mode: 'index',
      intersect: false,
      backgroundColor: 'rgba(15, 23, 42, 0.96)',
      titleColor: '#f8fafc',
      bodyColor: '#e2e8f0',
      borderColor: 'rgba(148, 163, 184, 0.4)',
      borderWidth: 1,
      padding: 14,
      cornerRadius: 10,
      titleFont: { size: 13, weight: '600' },
      bodyFont: { size: 12 },
      boxPadding: 6,
      displayColors: true,
    },
  };

  const verticalBarOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1.6,
    layout: { padding: { top: 16, right: 24, bottom: 16, left: 16 } },
    plugins: modernChartPlugins,
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: {
          maxRotation: 45,
          minRotation: 35,
          font: { size: 11, weight: '500' },
          color: '#64748b',
          padding: 10,
          maxTicksLimit: 18,
        },
        border: { display: false },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          font: { size: 11 },
          color: '#64748b',
          padding: 12,
        },
        grid: { color: 'rgba(226, 232, 240, 0.9)', lineWidth: 1, drawTicks: false },
        border: { display: false },
      },
    },
  };

  // Append "Current" point so chart never shows 0 when there are deficiencies
  const trendWithCurrent = [
    ...trend,
    { scan_timestamp: new Date().toISOString(), fail_count: deficient, finding_count: active, isCurrent: true }
  ];
  const trendLabels = trendWithCurrent.map((t, i) => {
    if (t.isCurrent) return 'Now';
    if (!t.scan_timestamp) return '';
    const d = new Date(t.scan_timestamp);
    const dateStr = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
    const prevSameDay = trendWithCurrent.slice(0, i).filter((x) => x.scan_timestamp && new Date(x.scan_timestamp).toDateString() === d.toDateString()).length;
    return prevSameDay > 0 ? `${dateStr} ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}` : dateStr;
  });
  const trendLineData = {
    labels: trendLabels,
    datasets: [
      {
        label: 'Deficient (fail count)',
        data: trendWithCurrent.map((t) => (t.fail_count != null ? Number(t.fail_count) : 0)),
        borderColor: '#dc2626',
        backgroundColor: 'rgba(220, 38, 38, 0.15)',
        fill: true,
        tension: 0.3,
        borderWidth: 2.5,
        pointRadius: 5,
        pointHoverRadius: 8,
        pointBackgroundColor: '#fff',
        pointBorderColor: '#dc2626',
        pointBorderWidth: 2,
      },
      {
        label: 'Findings (active)',
        data: trendWithCurrent.map((t) => (t.finding_count != null ? Number(t.finding_count) : 0)),
        borderColor: '#d97706',
        backgroundColor: 'rgba(217, 119, 6, 0.15)',
        fill: true,
        tension: 0.3,
        borderWidth: 2.5,
        pointRadius: 5,
        pointHoverRadius: 8,
        pointBackgroundColor: '#fff',
        pointBorderColor: '#d97706',
        pointBorderWidth: 2,
      },
    ],
  };

  const trendLineOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 2.2,
    layout: { padding: { top: 20, right: 24, bottom: 16, left: 16 } },
    plugins: modernChartPlugins,
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 11 }, color: '#64748b', padding: 10, maxRotation: 45 },
        border: { display: false },
      },
      y: {
        beginAtZero: true,
        suggestedMax: (() => {
          const allVals = trendWithCurrent.flatMap((t) => [t.fail_count ?? 0, t.finding_count ?? 0]);
          const maxVal = Math.max(0, ...allVals);
          return maxVal === 0 ? 5 : maxVal + Math.max(2, Math.ceil(maxVal * 0.2));
        })(),
        ticks: {
          stepSize: 1,
          font: { size: 11 },
          color: '#64748b',
          padding: 12,
        },
        grid: { color: 'rgba(226, 232, 240, 0.9)', lineWidth: 1, drawTicks: false },
        border: { display: false },
      },
    },
  };

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: true,
    cutout: '62%',
    layout: { padding: 20 },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 14,
          font: { ...MODERN_CHART_DEFAULTS.font, size: 12, weight: '500' },
          color: '#475569',
        },
      },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.96)',
        titleColor: '#f8fafc',
        bodyColor: '#e2e8f0',
        borderColor: 'rgba(148, 163, 184, 0.4)',
        borderWidth: 1,
        padding: 14,
        cornerRadius: 10,
        callbacks: { label: (ctx) => `${ctx.label}: ${ctx.raw} finding(s)` },
      },
    },
  };

  const hasIssues = deficient > 0 || fragile > 0;
  const latestScanStr = data?.latest_scan ? new Date(data.latest_scan.scan_timestamp).toLocaleString() : 'None';
  const effectiveReconciliation = lastScanReconciliation || data?.latest_scan_reconciliation || null;

  return (
    <div className="space-y-8 animate-fade-in">
      {effectiveReconciliation && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-4 text-sm text-indigo-900">
          <div className="font-semibold mb-1">Latest scan reconciliation</div>
          <div>
            Evaluated: <strong>{effectiveReconciliation.evaluated_results_count ?? 0}</strong> rows
            {' · '}Fail: <strong>{effectiveReconciliation.fail_results_count ?? 0}</strong>
            {' · '}Fragile: <strong>{effectiveReconciliation.fragile_results_count ?? 0}</strong>
            {' · '}Findings linked: <strong>{effectiveReconciliation.findings_linked_count ?? 0}</strong>
            {' · '}Service area: <strong>{effectiveReconciliation.service_area_count ?? 0}</strong>
            {effectiveReconciliation.service_area_fallback_used ? ' (fallback used)' : ''}
          </div>
          {Array.isArray(effectiveReconciliation.warnings) && effectiveReconciliation.warnings.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-xs text-indigo-800 space-y-0.5">
              {effectiveReconciliation.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          )}
        </div>
      )}
      {/* Hero: Clean minimal header with actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight">Network overview</h2>
          <p className="text-slate-500 text-sm mt-0.5">
            {hasIssues ? `${active} active findings require attention` : active === 0 ? 'No findings yet — run a scan to evaluate' : 'All clear'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {scanReady && (
            <button
              type="button"
              onClick={onRunScan}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-semibold shadow-sm hover:shadow transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              Run scan
            </button>
          )}
          <button type="button" onClick={onRefresh} className="px-4 py-2.5 bg-white hover:bg-slate-50 text-slate-700 rounded-xl text-sm font-medium border border-slate-200 hover:border-slate-300 transition-colors">
            Refresh
          </button>
          <button
            type="button"
            onClick={() => {
              const title = 'Network Adequacy — Executive Summary';
              const html = `<h1 style="font-size:1.5rem;margin-bottom:1rem">${title}</h1><p style="color:#64748b;font-size:0.875rem;margin-bottom:1.5rem">Generated ${new Date().toLocaleString()}</p><div style="display:grid;grid-template-columns:repeat(4,1fr);gap:1rem;margin-bottom:1.5rem"><div style="padding:1rem;background:#f8fafc;border-radius:0.5rem"><div style="font-size:1.5rem;font-weight:700">${active}</div><div style="font-size:0.75rem;color:#64748b">Active findings</div></div><div style="padding:1rem;background:#fff1f2;border-radius:0.5rem"><div style="font-size:1.5rem;font-weight:700;color:#e11d48">${deficient}</div><div style="font-size:0.75rem;color:#64748b">CMS-deficient</div></div><div style="padding:1rem;background:#fffbeb;border-radius:0.5rem"><div style="font-size:1.5rem;font-weight:700;color:#d97706">${fragile}</div><div style="font-size:0.75rem;color:#64748b">Fragile</div></div><div style="padding:1rem;background:#f8fafc;border-radius:0.5rem"><div style="font-size:0.875rem;font-weight:600">${latestScanStr}</div><div style="font-size:0.75rem;color:#64748b">Latest scan</div></div></div>${byLob.length > 0 ? `<h2 style="font-size:1rem;margin-bottom:0.5rem">By LOB</h2><table style="width:100%;border-collapse:collapse;font-size:0.875rem"><thead><tr style="text-align:left;border-bottom:1px solid #e2e8f0"><th style="padding:0.5rem">Contract</th><th style="padding:0.5rem;text-align:right">Deficient</th><th style="padding:0.5rem;text-align:right">Fragile</th><th style="padding:0.5rem;text-align:right">Total</th></tr></thead><tbody>${byLob.map((l) => `<tr style="border-bottom:1px solid #f1f5f9"><td style="padding:0.5rem">${l.lob_label}</td><td style="padding:0.5rem;text-align:right;color:#e11d48">${l.deficient_count}</td><td style="padding:0.5rem;text-align:right;color:#d97706">${l.fragile_count}</td><td style="padding:0.5rem;text-align:right">${l.total}</td></tr>`).join('')}</tbody></table>` : ''}`;
              const win = window.open('', '_blank');
              win.document.write(`<html><head><title>${title}</title><style>body{font-family:system-ui,sans-serif;padding:24px;max-width:800px;margin:0 auto;color:#1e293b}</style></head><body>${html}</body></html>`);
              win.document.close();
              win.print();
              win.close();
            }}
            className="px-4 py-2.5 bg-white hover:bg-slate-50 text-slate-700 rounded-xl text-sm font-medium border border-slate-200 hover:border-slate-300 transition-colors"
          >
            Export summary
          </button>
        </div>
      </div>

      {/* Headline adequacy percentages — the "industry standard" composite numbers */}
      <AdequacyHeadline
        headline={adequacyHeadline}
        programType={programType}
        onSelectProgramType={onSelectProgramType}
        applicationMode={applicationMode}
        onToggleApplicationMode={onToggleApplicationMode}
        apiBase={apiBase}
        onRefreshDashboard={onRefresh}
      />

      {/* Key metrics — F-pattern: most critical first */}
      <div id="na-dashboard-print" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <div
          className={`group relative overflow-hidden rounded-2xl border bg-white p-5 shadow-sm transition-all duration-200 hover:shadow-md hover:border-slate-300 cursor-pointer ${hasIssues && onSwitchToFindings ? 'ring-2 ring-offset-2 ring-red-400/50' : ''}`}
          onClick={hasIssues && onSwitchToFindings ? onSwitchToFindings : undefined}
          role={hasIssues && onSwitchToFindings ? 'button' : undefined}
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-slate-500 text-xs font-medium uppercase tracking-wider">CMS-deficient</p>
              <p className="text-3xl font-bold text-red-600 tracking-tight tabular-nums mt-1">{deficient}</p>
              <p className="text-slate-500 text-xs mt-1">Below required minimum</p>
            </div>
            <div className="rounded-full bg-red-50 p-2"><svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg></div>
          </div>
          {hasIssues && onSwitchToFindings && <p className="mt-3 text-xs font-medium text-red-600 group-hover:underline">View findings →</p>}
        </div>
        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-200 hover:shadow-md hover:border-amber-200">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-slate-500 text-xs font-medium uppercase tracking-wider">Fragile</p>
              <p className="text-3xl font-bold text-amber-600 tracking-tight tabular-nums mt-1">{fragile}</p>
              <p className="text-slate-500 text-xs mt-1">At or near minimum</p>
            </div>
            <div className="rounded-full bg-amber-50 p-2"><svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg></div>
          </div>
        </div>
        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-200 hover:shadow-md">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-slate-500 text-xs font-medium uppercase tracking-wider">Active total</p>
              <p className="text-3xl font-bold text-slate-800 tracking-tight tabular-nums mt-1">{active}</p>
              <p className="text-slate-500 text-xs mt-1">Open findings</p>
            </div>
          </div>
        </div>
        <div
          className={`group relative overflow-hidden rounded-2xl border p-5 shadow-sm transition-all duration-200 ${membersAtRiskTotal > 0 ? 'border-violet-200 bg-white hover:shadow-md hover:border-violet-300 cursor-pointer' : 'border-slate-200 bg-white'}`}
          onClick={membersAtRiskTotal > 0 ? () => onSwitchToFindings?.({ hasMemberImpact: true }) : undefined}
          role={membersAtRiskTotal > 0 ? 'button' : undefined}
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-slate-500 text-xs font-medium uppercase tracking-wider flex items-center gap-1">
                Members at risk <InfoTooltip text="Enrolled members in ZIP codes or counties with gaps. Based on your membership upload (or Census fallback)." />
              </p>
              {membersAtRiskTotal > 0 ? (
                <>
                  <p className="text-3xl font-bold text-violet-600 tracking-tight tabular-nums mt-1">{membersAtRiskTotal.toLocaleString()}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <p className="text-violet-600 text-xs font-medium group-hover:underline">View findings →</p>
                    {hasMembership && latestScanId && onRecomputeMembersAtRisk && (
                      <button
                        type="button"
                        disabled={recomputingMAR}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRecomputeMembersAtRisk(); }}
                        className="px-2 py-0.5 rounded bg-violet-100 hover:bg-violet-200 text-violet-700 text-xs font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {recomputingMAR ? 'Recomputing…' : recomputeMARSuccess ? 'Updated' : 'Recompute'}
                      </button>
                    )}
                  </div>
                  {recomputeMARError && <p className="mt-1 text-red-600 text-xs">{recomputeMARError}</p>}
                </>
              ) : hasMembership && latestScanId && onRecomputeMembersAtRisk ? (
                <div className="mt-1">
                  <p className="text-slate-500 text-sm">Recompute to refresh member impact.</p>
                  <button
                    type="button"
                    disabled={recomputingMAR}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRecomputeMembersAtRisk(); }}
                    className="mt-2 px-3 py-1.5 bg-violet-100 hover:bg-violet-200 disabled:opacity-60 disabled:cursor-not-allowed text-violet-700 text-xs font-medium rounded-lg"
                  >
                    {recomputingMAR ? 'Recomputing…' : recomputeMARSuccess ? 'Updated' : 'Recompute members at risk'}
                  </button>
                  {recomputeMARError && <p className="mt-1.5 text-red-600 text-xs">{recomputeMARError}</p>}
                </div>
              ) : (
                <p className="text-slate-500 text-sm mt-1">Upload membership and run scan to see member impact</p>
              )}
            </div>
            <div className="rounded-full bg-violet-50 p-2"><svg className="w-5 h-5 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg></div>
          </div>
        </div>
        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-200 hover:shadow-md">
          <div>
            <p className="text-slate-500 text-xs font-medium uppercase tracking-wider">Latest scan</p>
            <p className="text-lg font-semibold text-slate-800 mt-1 leading-tight">{latestScanStr}</p>
          </div>
        </div>
      </div>

      {topGapsByMembers.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden animate-slide-up">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Top gaps by member impact</h3>
              <p className="text-slate-500 text-sm mt-0.5">County · specialty · members at risk</p>
            </div>
            <button
              type="button"
              onClick={() => onSwitchToFindings?.({ hasMemberImpact: true })}
              className="text-sm font-medium text-violet-600 hover:text-violet-700 hover:underline"
            >
              View all with impact →
            </button>
          </div>
          <ul className="divide-y divide-slate-100">
            {topGapsByMembers.slice(0, 5).map((gap, i) => (
              <li key={i} className="px-6 py-3 flex items-center justify-between gap-4 hover:bg-slate-50/80 transition-colors">
                <span className="text-slate-800 font-medium">
                  {formatCountyDisplay(gap.ssa_state_county_code, gap.county_name, gap.state_name)}
                </span>
                <span className="text-slate-600 text-sm">
                  {gap.specialty_name || gap.specialty_code} {gap.result_type === 'facility' ? '(facility)' : ''}
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-violet-100 text-violet-800 text-sm font-medium shrink-0">
                  {gap.members_at_risk?.toLocaleString() ?? 0} members
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Debug — only when needed */}
      {false && (
        <div className="p-3 bg-slate-100 rounded-xl text-slate-600 text-xs font-mono border border-slate-200">
          <strong>[DEBUG]</strong> API active={active} deficient={deficient} fragile={fragile}
          {dbg ? ` | backend: na_findings rows=${dbg.findingsQueryRowCount}` : ''}
        </div>
      )}

      {(trend.length > 0 || active > 0 || deficient > 0) && (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-md overflow-hidden animate-slide-up">
          <div className="px-6 pt-6 pb-3 border-b border-slate-100 bg-gradient-to-b from-slate-50/50 to-white">
            <h3 className="text-base font-semibold text-slate-900">Trend over last scans</h3>
            <p className="text-slate-500 text-sm mt-0.5">Per-scan counts. Last point shows current state so it never drops to 0 when issues exist.</p>
          </div>
          <div className="min-h-[260px] px-6 pb-6 pt-4 bg-slate-50/30">
            <Line
              data={trendLineData}
              options={trendLineOptions}
            />
          </div>
        </div>
      )}

      {byLob.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden animate-slide-up">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="text-base font-semibold text-slate-900">Adequacy by LOB (contract)</h3>
            <p className="text-slate-500 text-sm mt-0.5">Active findings by contract/plan scope.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50/50">
                  <th className="pb-3 pt-3 px-6 font-medium">Contract / LOB</th>
                  <th className="pb-3 pt-3 px-4 font-medium text-right">Deficient</th>
                  <th className="pb-3 pt-3 px-4 font-medium text-right">Fragile</th>
                  <th className="pb-3 pt-3 px-6 font-medium text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {byLob.map((lob, i) => (
                  <tr key={lob.lob_key || i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/80 transition-colors">
                    <td className="py-3 px-6 font-medium text-slate-800">{lob.lob_label}</td>
                    <td className="py-3 px-4 text-right"><span className="inline-flex items-center justify-center min-w-[1.5rem] px-1.5 py-0.5 rounded-md text-red-600 font-semibold bg-red-50">{lob.deficient_count}</span></td>
                    <td className="py-3 px-4 text-right"><span className="inline-flex items-center justify-center min-w-[1.5rem] px-1.5 py-0.5 rounded-md text-amber-700 bg-amber-50">{lob.fragile_count}</span></td>
                    <td className="py-3 px-6 text-right text-slate-600 font-medium">{lob.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {hasCharts && (deficient > 0 || fragile > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 animate-slide-up">
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow transition-shadow">
            <div className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">Top risk county</div>
            <div className="font-semibold text-slate-900">
              {byCounty.length > 0 ? formatCountyDisplay(byCounty[0].ssa_state_county_code, byCounty[0].county_name, byCounty[0].state_name) : '—'}
            </div>
            {byCounty.length > 0 && (
              <div className="flex gap-2 mt-2">
                {byCounty[0].deficient_count > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">{byCounty[0].deficient_count} deficient</span>}
                {byCounty[0].fragile_count > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{byCounty[0].fragile_count} fragile</span>}
              </div>
            )}
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow transition-shadow">
            <div className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">Top failing specialty</div>
            <div className="font-semibold text-slate-900">
              {bySpecialty.length > 0 ? `${bySpecialty[0].specialty_name || bySpecialty[0].specialty_code} (${bySpecialty[0].result_type})` : '—'}
            </div>
            {bySpecialty.length > 0 && (
              <div className="flex gap-2 mt-2">
                {bySpecialty[0].deficient_count > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">{bySpecialty[0].deficient_count} deficient</span>}
                {bySpecialty[0].fragile_count > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{bySpecialty[0].fragile_count} fragile</span>}
              </div>
            )}
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow transition-shadow">
            <div className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">Totals</div>
            <div className="font-semibold text-slate-900">
              <span className="text-red-600">{deficient}</span> deficient · <span className="text-amber-600">{fragile}</span> fragile
            </div>
            <div className="text-xs text-slate-500 mt-1">{active} active findings</div>
          </div>
        </div>
      )}

      {hasCharts && (
        <div className="space-y-6 animate-slide-up">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Deficient counties & specialties</h3>
            <p className="text-slate-500 text-sm mt-0.5 max-w-2xl">
              Where active findings are concentrated. CMS-deficient = below required minimum; Fragile = at or near minimum.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-md overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-b from-slate-50/50 to-white">
                <h4 className="text-sm font-semibold text-slate-900">Findings by county</h4>
                <p className="text-slate-500 text-xs mt-0.5">Top 20 counties by active findings</p>
              </div>
              <div className="min-h-[300px] px-6 pb-6 pt-4 bg-slate-50/20">
                <Bar data={countyBarData} options={verticalBarOptions} />
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-md overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-b from-slate-50/50 to-white">
                <h4 className="text-sm font-semibold text-slate-900">Findings by specialty</h4>
                <p className="text-slate-500 text-xs mt-0.5">Top 15 specialties</p>
              </div>
              <div className="min-h-[300px] px-6 pb-6 pt-4 bg-slate-50/20">
                <Bar data={specialtyBarData} options={verticalBarOptions} />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-md p-6 flex flex-col items-center">
              <div className="px-4 py-3 border-b border-slate-100 w-full bg-gradient-to-b from-slate-50/50 to-white -mx-6 -mt-6 mb-4 rounded-t-2xl">
                <h4 className="text-sm font-semibold text-slate-900">Status breakdown</h4>
              </div>
              <div className="relative w-full max-w-[240px] min-h-[220px] flex items-center justify-center">
                <Doughnut data={donutData} options={donutOptions} />
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-3xl font-bold text-slate-800 tabular-nums">{statusBreakdown.total}</span>
                  <span className="text-xs text-slate-500 font-medium mt-0.5">active</span>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-md overflow-hidden lg:col-span-2">
              <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-b from-slate-50/50 to-white">
                <h4 className="text-sm font-semibold text-slate-900">Counties at a glance</h4>
                <p className="text-slate-500 text-xs mt-0.5">County compliance rate + worst status. Click to view findings.</p>
              </div>
              <ul className="divide-y divide-slate-100 max-h-52 overflow-y-auto">
                {byCounty.slice(0, 12).map((c, i) => (
                  <li
                    key={c.ssa_state_county_code || i}
                    role="button"
                    tabIndex={0}
                    onClick={onSwitchToFindings ? () => onSwitchToFindings() : undefined}
                    onKeyDown={(e) => onSwitchToFindings && (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onSwitchToFindings())}
                    className={`flex justify-between items-center py-3 px-6 hover:bg-slate-50 transition-colors ${onSwitchToFindings ? 'cursor-pointer' : 'cursor-default'}`}
                  >
                    <span className={`truncate mr-3 font-medium ${c.deficient_count > 0 ? 'text-slate-900' : 'text-slate-700'}`}>
                      {formatCountyDisplay(c.ssa_state_county_code, c.county_name, c.state_name)}
                    </span>
                    <span className="flex gap-2 shrink-0 items-center">
                      {c.adequacy_pct != null && (
                        <span
                          className={`text-xs font-semibold tabular-nums ${pctColor(c.adequacy_pct)}`}
                          title={c.cells_total != null ? `${c.cells_passing} of ${c.cells_total} specialty checks pass in this county` : 'County compliance rate'}
                        >
                          {formatPct(c.adequacy_pct)}
                        </span>
                      )}
                      {c.deficient_count > 0 && <span className="text-xs px-2 py-0.5 rounded-md bg-red-100 text-red-700 font-semibold">{c.deficient_count} deficient</span>}
                      {c.fragile_count > 0 && <span className="text-xs px-2 py-0.5 rounded-md bg-amber-100 text-amber-700">{c.fragile_count} fragile</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {!hasCharts && active === 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center animate-slide-up">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
            </div>
            {!data?.latest_scan ? (
              <>
                <h3 className="text-lg font-semibold text-slate-900">No scan has been run yet</h3>
                <p className="text-slate-500 text-sm mt-2">Upload provider and facility files in Uploads, then click Run scan to evaluate your network against CMS standards.</p>
                {scanReady && (
                  <button type="button" onClick={onRunScan} className="mt-6 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-sm transition-colors">
                    Run scan
                  </button>
                )}
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-slate-900">All clear</h3>
                <p className="text-slate-500 text-sm mt-2">Your last scan found no deficiencies. Run a new scan to re-evaluate.</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function UploadsSection({ batches, loading, onRefresh, onUploadSuccess, batchLabel, setBatchLabel, atbDeclared, setAtbDeclared, telehealthSpecialties, setTelehealthSpecialties, createBatch, creatingBatch }) {
  const [uploadingBatchId, setUploadingBatchId] = useState(null);
  const [uploadFileType, setUploadFileType] = useState('provider');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState(null);
  // Last uploaded NA file id — used to surface the PSI agent activity panel
  // (parser, geocoder, exclusion check, etc.) while the file is being processed.
  const [lastFileId, setLastFileId] = useState(null);
  const fileInputRef = useRef(null);

  const handleUploadSubmit = (e) => {
    e.preventDefault();
    if (!uploadingBatchId || !uploadFile) return;
    setUploading(true);
    setUploadMessage(null);
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('batch_id', uploadingBatchId);
    formData.append('file_type', uploadFileType);
    fetch(`${NA}/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData
    })
      .then(async (r) => {
        const text = await r.text();
        try {
          return { ok: r.ok, status: r.status, data: JSON.parse(text) };
        } catch {
          throw new Error(r.ok ? 'Invalid response from server' : `Upload failed: ${r.status} ${r.statusText}. Large file? Try a smaller file or check server logs.`);
        }
      })
      .then(({ ok, data: d }) => {
        if (d?.success) {
          setUploadMessage(d.parse_status === 'success' ? `Uploaded: ${d.summary?.valid ?? 0} valid rows` : `Uploaded with warnings: ${d.summary?.valid ?? 0} valid, ${d.summary?.quarantined ?? 0} quarantined`);
          if (d.file_id) setLastFileId(d.file_id);
          setUploadFile(null);
          setUploadingBatchId(null);
          if (fileInputRef.current) fileInputRef.current.value = '';
          onRefresh();
          if (onUploadSuccess) onUploadSuccess();
        } else {
          setUploadMessage(d?.message || (ok ? 'Upload failed' : 'Server error. Large file? Check server logs.'));
        }
        setUploading(false);
      })
      .catch((err) => {
        setUploadMessage(err.message || 'Upload failed');
        setUploading(false);
      });
  };

  const cancelUpload = () => {
    setUploadingBatchId(null);
    setUploadFile(null);
    setUploadMessage(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-orange-600 via-orange-500 to-amber-500 rounded-2xl shadow-lg p-5">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-lg font-bold text-white">CMS files, loaded directly</h2>
              <p className="text-[11.5px] text-white/85">For files already in the exact HPMS provider and facility table layout. A roster in any other layout goes in on the Provider Roster tab, which builds these tables for you.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Batch label (optional)"
              value={batchLabel}
              onChange={(e) => setBatchLabel(e.target.value)}
              className="rounded-xl border border-white/30 bg-white/10 text-white placeholder-white/60 px-3 py-2 text-sm w-48"
            />
            <label className="flex items-center gap-2 text-white text-sm font-medium cursor-pointer" title="Additional telehealth benefits declaration (42 CFR 422.116(d)(5)): applies the +10 point credit to eligible specialties for this submission.">
              <input
                type="checkbox"
                checked={atbDeclared}
                onChange={(e) => setAtbDeclared(e.target.checked)}
                className="rounded border-white/40"
              />
              ATB telehealth declared
            </label>
            <button type="button" onClick={createBatch} disabled={creatingBatch} className="px-4 py-2 bg-white/20 hover:bg-white/30 disabled:opacity-50 text-white rounded-xl text-sm font-medium">
              {creatingBatch ? 'Creating…' : 'New batch'}
            </button>
          </div>
        </div>
        {atbDeclared && (
          <div className="mt-3 rounded-xl bg-white/10 p-3">
            <div className="text-white text-xs font-semibold mb-1">Telehealth specialties indicated to CMS (HPMS Submit Network). Only these receive the 10 point credit.</div>
            <TelehealthSpecialtyPicker value={telehealthSpecialties} onChange={setTelehealthSpecialties} light />
            {!telehealthSpecialties.length && <div className="mt-1 text-[11.5px] text-amber-200">None indicated: the plan's standing list from Settings is used; if that is empty too, no telehealth credit is applied.</div>}
          </div>
        )}
        <p className="text-white/90 text-sm mt-3">
          Use MA_Provider_Table_Template and MA_Facility_Table_Template Excel files. Create a batch, then upload provider and/or facility files below. Check "ATB telehealth declared" when the plan offers additional telehealth benefits (422.135) and tick the specialties it indicated to CMS; those receive the CMS +10 point credit.
        </p>
      </div>
      {batches.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg border-2 border-violet-200 p-5">
          <h3 className="text-base font-bold text-slate-800 mb-1 flex items-center gap-2">
            Upload a file to a batch
          </h3>
          <p className="text-slate-600 text-sm mb-4">Choose a batch, then select Provider or Facility and pick your Excel file.</p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const batchId = e.target.querySelector('[name="upload_batch_id"]')?.value;
              const fileType = e.target.querySelector('[name="upload_file_type"]')?.value;
              const fileInput = e.target.querySelector('[name="upload_file_input"]');
              const file = fileInput?.files?.[0];
              if (!batchId || !fileType || !file) return;
              setUploadingBatchId(batchId);
              setUploadFileType(fileType);
              setUploadFile(file);
              setUploadMessage(null);
              setUploading(true);
              const formData = new FormData();
              formData.append('file', file);
              formData.append('batch_id', batchId);
              formData.append('file_type', fileType);
              fetch(`${NA}/upload`, { method: 'POST', credentials: 'include', body: formData })
                .then(async (r) => {
                  const text = await r.text();
                  try {
                    return { ok: r.ok, data: JSON.parse(text) };
                  } catch {
                    throw new Error(r.ok ? 'Invalid response' : `Upload failed: ${r.status}. Large file? Try smaller or check server logs.`);
                  }
                })
                .then(({ data: d }) => {
                  if (d?.success) {
                    setUploadMessage(d.parse_status === 'success' ? `Uploaded: ${d.summary?.valid ?? 0} valid rows` : `Uploaded: ${d.summary?.valid ?? 0} valid, ${d.summary?.quarantined ?? 0} quarantined`);
                    if (d.file_id) setLastFileId(d.file_id);
                    setUploadingBatchId(null); setUploadFile(null); if (fileInput) fileInput.value = '';
                    onRefresh();
                    if (onUploadSuccess) onUploadSuccess();
                  } else setUploadMessage(d?.message || 'Upload failed');
                  setUploading(false);
                })
                .catch((err) => { setUploadMessage(err.message || 'Upload failed'); setUploading(false); });
            }}
            className="flex flex-wrap items-end gap-4"
          >
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">Batch</label>
              <select name="upload_batch_id" required className="rounded-xl border border-slate-200 bg-white text-slate-800 px-3 py-2.5 text-sm min-w-[200px]">
                <option value="">Select a batch</option>
                {batches.map((b) => (
                  <option key={b.id} value={b.id}>{b.label || b.id}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">File type</label>
              <select name="upload_file_type" className="rounded-xl border border-slate-200 bg-white text-slate-800 px-3 py-2.5 text-sm">
                <option value="provider">Provider (MA_Provider_Table_Template)</option>
                <option value="facility">Facility (MA_Facility_Table_Template)</option>
              </select>
            </div>
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">Excel or tab-delimited file (.xlsx, .xls, .txt)</label>
              <input name="upload_file_input" type="file" accept=".xlsx,.xls,.txt" required className="block text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:bg-violet-600 file:text-white file:text-sm file:font-medium" />
            </div>
            <button type="submit" disabled={uploading} className="px-5 py-2.5 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white rounded-xl text-sm font-bold">
              {uploading ? 'Uploading…' : 'Upload file'}
            </button>
          </form>
        </div>
      )}
      {uploadMessage && (
        <div className={`px-4 py-2 rounded-xl text-sm ${uploadMessage.startsWith('Uploaded') ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
          {uploadMessage}
        </div>
      )}
      {/* Agent activity for the most recent upload — renders nothing until
          the parser/geocoder/exclusion agents emit progress events. */}
      {lastFileId && <NAUploadAgentActivity fileId={lastFileId} />}
      {loading ? (
        <div className="text-slate-500">Loading batches…</div>
      ) : (
        <ul className="space-y-2">
          {batches.length === 0 ? (
            <li className="text-slate-500 py-4 px-4 bg-white rounded-xl shadow border border-slate-200">No batches yet. Create one above, then upload files to it.</li>
          ) : (
            batches.map((b) => (
              <li key={b.id} className="bg-white rounded-xl shadow border border-slate-200 overflow-hidden">
                <div className="flex items-center justify-between py-3 px-4">
                  <span className="text-slate-800 font-medium">{b.label || b.id}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-slate-500 text-sm">{b.file_count} file(s) · {new Date(b.created_at).toLocaleString()}</span>
                    <button
                      type="button"
                      onClick={() => { setUploadingBatchId(uploadingBatchId === b.id ? null : b.id); setUploadFile(null); setUploadMessage(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                      className="px-3 py-1.5 bg-violet-600 hover:bg-violet-500 text-white rounded-lg text-sm font-medium"
                    >
                      {uploadingBatchId === b.id ? 'Cancel' : 'Upload file'}
                    </button>
                  </div>
                </div>
                {uploadingBatchId === b.id && (
                  <form onSubmit={handleUploadSubmit} className="border-t border-slate-100 bg-slate-50/80 px-4 py-4 flex flex-wrap items-end gap-4">
                    <div>
                      <label className="block text-slate-600 text-xs font-medium mb-1">File type</label>
                      <select
                        value={uploadFileType}
                        onChange={(e) => setUploadFileType(e.target.value)}
                        className="rounded-lg border border-slate-200 bg-white text-slate-800 px-3 py-2 text-sm"
                      >
                        <option value="provider">Provider (MA_Provider_Table_Template)</option>
                        <option value="facility">Facility (MA_Facility_Table_Template)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-slate-600 text-xs font-medium mb-1">Excel or tab-delimited file (.xlsx, .xls, .txt)</label>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls,.txt"
                        onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                        className="block text-sm text-slate-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-violet-100 file:text-violet-800 file:text-sm file:font-medium"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={!uploadFile || uploading}
                      className="px-4 py-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
                    >
                      {uploading ? 'Uploading…' : 'Upload'}
                    </button>
                  </form>
                )}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// NAUploadAgentActivity — polls /api/agent-progress for an in-flight NA file
// upload. Mirrors the OracleHome DocumentAgentActivity pattern.
// ---------------------------------------------------------------------------
function NAUploadAgentActivity({ fileId }) {
  const { events, status } = useAgentProgress('na_file', fileId);
  if (!events || events.length === 0) return null;
  return <AgentThinkingPanel events={events} status={status} title="Activity" />;
}

function FindingsSection({ findings, debugFindings, loading, onRefresh, selectedId, onSelectFinding, apiBase, onSwitchToDrafts, sort, onSortChange, filterImpact, onFilterImpactChange }) {
  const [filterStatus, setFilterStatus] = useState('');
  const filtered = filterStatus ? findings.filter((f) => f.status === filterStatus) : findings;

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-orange-600 via-orange-500 to-amber-500 rounded-2xl shadow-lg p-5">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-lg font-bold text-white">Findings</h2>
              <p className="text-white/90 text-xs mt-0.5">CMS-deficient = below requirement. Fragile = meets minimum but at risk. Click a row for full detail and actions.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={sort}
              onChange={(e) => onSortChange?.(e.target.value)}
              className="rounded-xl border border-white/30 bg-white/10 text-white px-3 py-2 text-sm"
              title="Sort findings"
            >
              <option value="">Sort by date</option>
              <option value="members_at_risk_desc">Sort by Members at Risk (high first)</option>
            </select>
            <select
              value={filterImpact}
              onChange={(e) => onFilterImpactChange?.(e.target.value)}
              className="rounded-xl border border-white/30 bg-white/10 text-white px-3 py-2 text-sm"
              title="Filter by member impact"
            >
              <option value="">All findings</option>
              <option value="has_impact">Has member impact</option>
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="rounded-xl border border-white/30 bg-white/10 text-white px-3 py-2 text-sm"
            >
              <option value="">All statuses</option>
              <option value="open">Open</option>
              <option value="in_remediation">In remediation</option>
              <option value="ready_for_closure">Ready for closure</option>
              <option value="closed">Closed</option>
            </select>
            <button type="button" onClick={onRefresh} className="text-sm text-white/90 hover:text-white">Refresh</button>
          </div>
        </div>
      </div>
      {loading ? (
        <div className="text-slate-500">Loading findings…</div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-2xl shadow border border-slate-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-600 border-b border-slate-200 bg-slate-50/80">
                <th className="pb-3 pt-3 pl-4 pr-4 font-semibold">Summary</th>
                <th className="pb-3 pt-3 pr-4 font-semibold">Severity</th>
                <th className="pb-3 pt-3 pr-4 font-semibold">Members at Risk</th>
                <th className="pb-3 pt-3 pr-4 font-semibold">Required / Found</th>
                <th className="pb-3 pt-3 pr-4 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((f) => (
                <tr
                  key={f.id}
                  onClick={() => onSelectFinding(f.id)}
                  className={`border-b border-slate-100 hover:bg-violet-50/50 cursor-pointer ${selectedId === f.id ? 'bg-violet-100/70' : ''}`}
                >
                  <td className="py-3 pl-4 pr-4">
                    <div className="text-slate-800 font-medium max-w-md">{f.plain_summary || f.title || f.id}</div>
                    <div className="text-slate-500 text-xs mt-0.5">{formatCountyDisplay(f.ssa_state_county_code, f.county_name, f.state_name)} · {f.specialty_name || f.specialty_code} · {f.result_type}</div>
                  </td>
                  <td className="py-3 pr-4">
                    {f.cms_baseline_status === 'fail' ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-red-100 text-red-800 text-xs font-semibold" title="Does not meet CMS network adequacy requirement">CMS-deficient</span>
                    ) : f.internal_fragile_status === 'fragile' ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 text-xs font-semibold" title="Meets minimum but close to threshold; consider adding capacity">Fragile</span>
                    ) : (
                      <span className="text-slate-500 text-xs">—</span>
                    )}
                  </td>
                  <td className="py-3 pr-4 text-slate-600">
                    {f.members_at_risk != null ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-violet-100 text-violet-800 text-xs font-medium" title={f.membership_source ? `Based on ${f.membership_source} data` : ''}>{f.members_at_risk.toLocaleString()}</span>
                    ) : (
                      <span className="text-slate-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="py-3 pr-4 text-slate-600">
                    {f.why_failed ? `${f.why_failed.required_count} required / ${f.why_failed.actual_count} found` : '—'}
                  </td>
                  <td className="py-3 pr-4 text-slate-600">{f.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="text-slate-500 py-6 px-4">No findings.</p>}
        </div>
      )}
      {selectedId && (
        <FindingDetailModal findingId={selectedId} apiBase={apiBase} onClose={() => onSelectFinding(null)} onUpdated={onRefresh} onSwitchToDrafts={onSwitchToDrafts} />
      )}
    </div>
  );
}

function FindingDetailModal({ findingId, apiBase, onClose, onUpdated, onSwitchToDrafts }) {
  const [detail, setDetail] = useState(null);
  const [assignUserIds, setAssignUserIds] = useState('');
  const [people, setPeople] = useState([]);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL || ''}/api/na-roster/people`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => d.success && setPeople(d.people || [])).catch(() => {});
  }, []);
  const [updateText, setUpdateText] = useState('');
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [auditTrail, setAuditTrail] = useState(null);
  const [recruitmentNpi, setRecruitmentNpi] = useState('');
  const [recruitmentName, setRecruitmentName] = useState('');
  const [recruitmentStatus, setRecruitmentStatus] = useState('targeting');
  const [recruitmentNotes, setRecruitmentNotes] = useState('');

  useEffect(() => {
    if (!findingId) return;
    fetch(`${apiBase}/findings/${findingId}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => d.success && setDetail(d));
  }, [findingId, apiBase]);

  useEffect(() => {
    if (!findingId || !apiBase) return;
    fetch(`${apiBase}/findings/${findingId}/audit-trail`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => d.success && setAuditTrail(d));
  }, [findingId, apiBase]);

  if (!detail) return null;

  const { finding, causes, assignments, recruitment = [] } = detail;

  const handleAssign = () => {
    const ids = assignUserIds.split(/[\s,]+/).filter(Boolean);
    if (ids.length === 0) return;
    fetch(`${apiBase}/findings/${findingId}/assign`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_ids: ids })
    }).then(() => { setAssignUserIds(''); onUpdated(); });
  };

  const handleAddUpdate = () => {
    if (!updateText.trim()) return;
    fetch(`${apiBase}/findings/${findingId}/updates`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ update_text: updateText })
    }).then(() => { setUpdateText(''); onUpdated(); });
  };

  const handleReadyForClosure = () => {
    fetch(`${apiBase}/findings/${findingId}/ready-for-closure`, { method: 'PATCH', credentials: 'include' }).then(onUpdated);
  };

  const handleClose = () => {
    fetch(`${apiBase}/findings/${findingId}/close`, { method: 'PATCH', credentials: 'include' }).then(() => { onUpdated(); onClose(); });
  };

  const refetchDetail = () => {
    fetch(`${apiBase}/findings/${findingId}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => d.success && setDetail(d));
  };

  const handleAddRecruitment = () => {
    if (!recruitmentNpi.trim() && !recruitmentName.trim()) return;
    fetch(`${apiBase}/findings/${findingId}/recruitment`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ provider_npi: recruitmentNpi.trim() || null, provider_name: recruitmentName.trim() || null, status: recruitmentStatus, notes: recruitmentNotes.trim() || null })
    }).then((r) => r.json()).then((d) => {
      if (d.success) { setRecruitmentNpi(''); setRecruitmentName(''); setRecruitmentNotes(''); refetchDetail(); onUpdated(); }
    });
  };

  const handleUpdateRecruitmentStatus = (recId, status) => {
    fetch(`${apiBase}/findings/${findingId}/recruitment/${recId}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    }).then(() => { refetchDetail(); onUpdated(); });
  };

  const handleDeleteRecruitment = (recId) => {
    fetch(`${apiBase}/findings/${findingId}/recruitment/${recId}`, { method: 'DELETE', credentials: 'include' }).then(() => { refetchDetail(); onUpdated(); });
  };

  const why = finding?.why_failed;
  const isDeficient = finding?.cms_baseline_status === 'fail';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl border border-slate-200 max-w-2xl w-full max-h-[90vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start gap-4 mb-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              {isDeficient ? (
                <span className="inline-flex px-2 py-0.5 rounded-md bg-red-100 text-red-800 text-xs font-semibold">CMS-deficient</span>
              ) : finding?.internal_fragile_status === 'fragile' ? (
                <span className="inline-flex px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 text-xs font-semibold">Fragile</span>
              ) : null}
              {finding?.members_at_risk != null && (
                <span className="inline-flex px-2 py-0.5 rounded-md bg-violet-100 text-violet-800 text-xs font-medium" title={finding?.membership_source ? `Based on ${finding.membership_source} data${finding.members_at_risk_updated_at ? ` as of ${new Date(finding.members_at_risk_updated_at).toLocaleDateString()}` : ''}` : ''}>
                  {finding.members_at_risk.toLocaleString()} members at risk
                </span>
              )}
              <span className="text-slate-500 text-xs">{finding?.status}</span>
            </div>
            <h3 className="text-lg font-semibold text-slate-800 leading-snug">{finding?.plain_summary || finding?.title}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-800 text-xl leading-none flex-shrink-0">×</button>
        </div>

        {why && (
          <div className="mb-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
            <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Why it failed</h4>
            <p className="text-slate-700 text-sm mb-2">{why.rule_description}</p>
            <p className="text-slate-700 font-semibold">Required: {why.required_count} · Found: {why.actual_count}</p>
            {finding?.members_at_risk != null && (
              <p className="text-slate-600 text-sm mt-1">Members affected: {finding.members_at_risk.toLocaleString()}{finding.membership_source ? ` (${finding.membership_source} data)` : ''}</p>
            )}
            {why.county_type_label && (
              <p className="text-slate-600 text-xs mt-1">County type used: {why.county_type_label}</p>
            )}
            {why.coverage_method && (
              <p className="text-slate-600 text-xs mt-1 flex items-center gap-1">
                Coverage method:{' '}
                <span className={
                  why.coverage_method === 'enrollment_weighted' ? 'text-violet-700 font-medium'
                  : why.coverage_method === 'population_weighted' ? 'text-emerald-700 font-medium'
                  : 'text-amber-700 font-medium'
                }>
                  {why.coverage_method === 'enrollment_weighted' ? 'Enrollment-weighted (plan beneficiaries)'
                    : why.coverage_method === 'population_weighted' ? 'Population-weighted (Census data)'
                    : 'Uniform (no population data)'}
                </span>
                <InfoTooltip text={
                  why.coverage_method === 'enrollment_weighted'
                    ? 'Coverage % is weighted by your uploaded plan enrollment (member counts by ZIP) — this is the closest measure to what CMS expects: % of actual beneficiaries within standard. Strongest for audit defense.'
                    : why.coverage_method === 'population_weighted'
                    ? 'Coverage % is weighted by Census ZCTA population — closer to what CMS expects (% of beneficiaries within standard). Load plan enrollment in Settings for even better accuracy.'
                    : 'All sample locations are weighted equally. Load plan enrollment or Census ZCTA data in Settings for population-weighted coverage, which better reflects % of actual beneficiaries.'
                } />
              </p>
            )}
            {why.time_distance_note && (
              <p className="text-slate-500 text-xs mt-1 italic">Note: {why.time_distance_note}</p>
            )}
            {(why.pct_population_within_time_distance_standard != null || why.actual_drive_minutes != null) && (
              <div className="mt-2 pt-2 border-t border-slate-200">
                <p className="text-xs font-semibold text-slate-600 flex items-center gap-1">
                  Drive time & distance
                  <InfoTooltip text="We measured drive time from sample member locations to the nearest provider. Too few locations met the CMS standard." />
                </p>
                {why.pct_within_distance != null && (
                  <p className="text-slate-600 text-xs">
                    Beneficiaries within the CMS distance: {why.pct_within_distance}%{why.distance_met === false ? ' (below threshold)' : ''}
                    {why.pct_within_time != null && <> · within the CMS time: {why.pct_within_time}%{why.time_met === false ? ' (below threshold)' : ''}</>}
                    <InfoTooltip text="HPMS measures distance and time as two separate shares of the county's Medicare beneficiaries (CMS sample census); each must meet the county threshold. Time applies in Large Metro counties only." />
                  </p>
                )}
                {why.pct_within_distance == null && why.pct_population_within_time_distance_standard != null && (
                  <p className="text-slate-600 text-xs">
                    Member locations within standard: {why.pct_population_within_time_distance_standard}%
                    <InfoTooltip text="% of sample locations (ZIP centers, Census points) where the nearest provider is within the allowed drive time/distance. CMS requires 90% (metro) or 85% (rural)." />
                  </p>
                )}
                {why.time_distance_access_point_source && (
                  <p className="text-slate-500 text-xs">
                    Location source: {why.time_distance_access_point_source}
                    <InfoTooltip text="Where we got the sample locations: ZIP centroids, Census points, or county center." />
                  </p>
                )}
                {(why.actual_drive_minutes != null || why.actual_drive_miles != null) && (
                  <p className="text-slate-600 text-xs">
                    Worst-case (farthest location): {why.actual_drive_minutes != null ? `~${Math.round(why.actual_drive_minutes)} min` : '—'} / {why.actual_drive_miles != null ? `${Number(why.actual_drive_miles).toFixed(1)} mi` : '—'}
                    <InfoTooltip text={`Drive time and distance to the nearest provider for the access point that failed. Calculated using ${(why.time_distance_calculation_method || 'routing') === 'haversine_fallback' ? 'distance estimate' : 'routing API'} (Google, Mapbox, or OSRM).`} />
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {finding?.impact && (
          <div className="mb-4">
            <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1">Impact</h4>
            <p className="text-slate-700 text-sm">{finding.impact}</p>
          </div>
        )}

        {finding?.what_to_do?.length > 0 && (
          <div className="mb-4">
            <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">What to do</h4>
            <ul className="list-disc list-inside text-slate-700 text-sm space-y-1">
              {finding.what_to_do.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        {causes?.length > 0 && (
          <div className="mb-4">
            <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1">Technical causes</h4>
            <ul className="list-disc list-inside text-slate-600 text-sm">{causes.map((c, i) => <li key={i}>{c.cause_description}</li>)}</ul>
          </div>
        )}

        {assignments?.length > 0 && (
          <div className="mb-4">
            <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1">Assignees</h4>
            <ul className="text-slate-600 text-sm">{assignments.map((a) => <li key={a.id}>{a.assignee_name} ({a.assignee_email})</li>)}</ul>
          </div>
        )}
        {auditTrail && (
          <div className="mb-4">
            <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Evidence chain</h4>
            {(auditTrail.evidence || []).length > 0 ? (
              <ul className="text-sm text-slate-700 space-y-1 mb-2">
                {auditTrail.evidence.map((e) => (
                  <li key={e.id}>
                    <a
                      href="#"
                      onClick={(ev) => {
                        ev.preventDefault();
                        fetch(`${apiBase}/findings/${findingId}/evidence/${e.id}/download`, { credentials: 'include' })
                          .then((r) => r.blob())
                          .then((blob) => {
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = e.file_name || 'evidence';
                            a.click();
                            URL.revokeObjectURL(url);
                          });
                      }}
                      className="text-violet-600 hover:underline"
                    >
                      {e.file_name}
                    </a>
                    <span className="text-slate-500 text-xs ml-1">({e.file_size_bytes ? `${(e.file_size_bytes / 1024).toFixed(1)} KB` : ''} · {e.created_at ? new Date(e.created_at).toLocaleString() : ''})</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-500 text-sm">No evidence attached</p>
            )}
            <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500 mt-3 mb-1">Audit events</h4>
            <ul className="space-y-1 text-xs text-slate-600 max-h-24 overflow-y-auto">
              {(auditTrail.audit_events || []).slice(0, 10).map((ev, i) => (
                <li key={i}><span className="text-slate-500">{ev.created_at ? new Date(ev.created_at).toLocaleString() : ''}</span> · {ev.action_type}: {ev.description || ''}</li>
              ))}
              {(auditTrail.audit_events?.length || 0) > 10 && <li className="text-slate-500">… and {auditTrail.audit_events.length - 10} more</li>}
            </ul>
          </div>
        )}
        <div className="mb-4">
          <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Provider recruitment</h4>
          <p className="text-slate-600 text-xs mb-2">Track recruitment efforts to fill this gap.</p>
          <div className="flex flex-wrap gap-2 mb-2">
            <input
              type="text"
              placeholder="Provider NPI"
              value={recruitmentNpi}
              onChange={(e) => setRecruitmentNpi(e.target.value)}
              className="rounded border border-slate-200 px-2 py-1.5 text-sm w-28"
            />
            <input
              type="text"
              placeholder="Provider name"
              value={recruitmentName}
              onChange={(e) => setRecruitmentName(e.target.value)}
              className="rounded border border-slate-200 px-2 py-1.5 text-sm flex-1 min-w-[120px]"
            />
            <select value={recruitmentStatus} onChange={(e) => setRecruitmentStatus(e.target.value)} className="rounded border border-slate-200 px-2 py-1.5 text-sm">
              <option value="targeting">Targeting</option>
              <option value="contacted">Contacted</option>
              <option value="negotiating">Negotiating</option>
              <option value="contracted">Contracted</option>
            </select>
            <input
              type="text"
              placeholder="Notes"
              value={recruitmentNotes}
              onChange={(e) => setRecruitmentNotes(e.target.value)}
              className="rounded border border-slate-200 px-2 py-1.5 text-sm flex-1 min-w-[100px]"
            />
            <button type="button" onClick={handleAddRecruitment} disabled={!recruitmentNpi.trim() && !recruitmentName.trim()} className="px-3 py-1.5 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white rounded text-sm font-medium">Add</button>
          </div>
          {recruitment.length > 0 ? (
            <ul className="space-y-2">
              {recruitment.map((r) => (
                <li key={r.id} className="flex items-center gap-2 flex-wrap p-2 bg-slate-50 rounded-lg border border-slate-100">
                  <span className="font-medium text-slate-800">{r.provider_name || r.provider_npi || '—'}</span>
                  {r.provider_npi && <span className="text-slate-500 text-xs">NPI: {r.provider_npi}</span>}
                  <select value={r.status} onChange={(e) => handleUpdateRecruitmentStatus(r.id, e.target.value)} className="rounded border border-slate-200 px-2 py-0.5 text-xs">
                    <option value="targeting">Targeting</option>
                    <option value="contacted">Contacted</option>
                    <option value="negotiating">Negotiating</option>
                    <option value="contracted">Contracted</option>
                  </select>
                  {r.notes && <span className="text-slate-600 text-xs flex-1 truncate" title={r.notes}>{r.notes}</span>}
                  <span className="text-slate-400 text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</span>
                  <button type="button" onClick={() => handleDeleteRecruitment(r.id)} className="text-slate-400 hover:text-red-600 text-xs">×</button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-slate-500 text-sm">No recruitment records yet</p>
          )}
        </div>
        {onSwitchToDrafts && (
          <div className="mb-4">
            <button
              type="button"
              disabled={creatingDraft}
              onClick={() => {
                setCreatingDraft(true);
                fetch(`${apiBase}/exception-drafts`, {
                  method: 'POST',
                  credentials: 'include',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ finding_id: findingId, scan_id: finding?.scan_id ?? null })
                })
                  .then((r) => r.json())
                  .then((d) => {
                    setCreatingDraft(false);
                    if (d.success && d.draft?.id) {
                      onSwitchToDrafts(d.draft.id);
                      onClose();
                      onUpdated();
                    }
                  })
                  .catch(() => setCreatingDraft(false));
              }}
              className="px-4 py-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white rounded-xl text-sm font-medium"
            >
              {creatingDraft ? 'Creating…' : 'Create exception draft'}
            </button>
            <p className="text-slate-500 text-xs mt-1">Start a CMS-style exception package linked to this finding. You’ll be taken to Exception Drafts to edit and export.</p>
          </div>
        )}
        <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
          <div className="flex gap-2">
            <select
              value={assignUserIds}
              onChange={(e) => setAssignUserIds(e.target.value)}
              className="flex-1 rounded-xl border border-slate-200 bg-slate-50 text-slate-800 px-3 py-2 text-sm"
            >
              <option value="">Assign this finding to…</option>
              {people.map((p) => <option key={p.id} value={p.id}>{p.name !== p.email ? `${p.name} (${p.email})` : p.name}</option>)}
            </select>
            <button type="button" onClick={handleAssign} className="px-3 py-2 bg-violet-600 hover:bg-violet-500 text-white rounded-xl text-sm font-medium">Assign</button>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Add update note"
              value={updateText}
              onChange={(e) => setUpdateText(e.target.value)}
              className="flex-1 rounded-xl border border-slate-200 bg-slate-50 text-slate-800 px-3 py-2 text-sm"
            />
            <button type="button" onClick={handleAddUpdate} className="px-3 py-2 bg-violet-600 hover:bg-violet-500 text-white rounded-xl text-sm font-medium">Add update</button>
          </div>
          {finding?.status !== 'closed' && (
            <div className="flex gap-2">
              <button type="button" onClick={handleReadyForClosure} className="px-3 py-2 bg-amber-500 hover:bg-amber-400 text-white rounded-xl text-sm font-medium">Mark ready for closure</button>
              {finding?.status === 'ready_for_closure' && (
                <button type="button" onClick={handleClose} className="px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-sm font-medium">Approve closure</button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ScanAuditTrailModal({ scanId, apiBase, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!scanId || !apiBase) return;
    setLoading(true);
    fetch(`${apiBase}/scans/${scanId}/audit-trail`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [scanId, apiBase]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl border border-slate-200 max-w-3xl w-full max-h-[90vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-semibold text-slate-800">Scan audit trail</h3>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-800 text-xl leading-none">×</button>
        </div>
        {loading && <p className="text-slate-500">Loading…</p>}
        {!loading && data?.success && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Scan {scanId} · {data.scan_timestamp ? new Date(data.scan_timestamp).toLocaleString() : ''}</p>
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Method & confidence per result (sample)</h4>
              <div className="max-h-48 overflow-y-auto text-xs font-mono bg-slate-50 rounded-lg p-3 space-y-1">
                {(data.results || []).slice(0, 10).map((r, i) => (
                  <div key={i} className="flex flex-wrap gap-x-3">
                    <span>{r.ssa_state_county_code} / {r.specialty_code} / {r.result_type}</span>
                    <span>coverage: {r.method?.coverage_method || '—'}</span>
                    <span>route: {r.method?.time_distance_calculation_method || '—'}</span>
                    <span>confidence: {r.confidence?.route_confidence || '—'}</span>
                    {r.confidence?.td_verification_required && <span className="text-amber-600">verification required</span>}
                  </div>
                ))}
                {(data.results?.length || 0) > 10 && <p className="text-slate-500 pt-1">… and {data.results.length - 10} more</p>}
              </div>
            </div>
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Audit events</h4>
              <ul className="space-y-1 text-sm text-slate-700">
                {(data.audit_events || []).map((e, i) => (
                  <li key={i}><span className="text-slate-500">{new Date(e.created_at).toLocaleString()}</span> · {e.action_type}: {e.description || ''}</li>
                ))}
                {(data.audit_events?.length || 0) === 0 && <li className="text-slate-500">No audit events</li>}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ScenarioModal({ scanId, apiBase, onClose }) {
  // What if: take providers out of this run, or add providers that do not exist yet,
  // and see which county and specialty standards would change. Counts only; drive
  // time is not re-measured here.
  const [mapData, setMapData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [removeIds, setRemoveIds] = useState([]);
  const [adds, setAdds] = useState([]);
  const [draft, setDraft] = useState({ county: '', specialty: '', count: 1 });
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!scanId || !apiBase) return;
    setLoading(true);
    Promise.all([
      fetch(`${apiBase}/map-data?scan_id=${scanId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
      fetch(`${API}/api/na-roster/adequacy/summary`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
    ]).then(([m, s]) => { setMapData(m?.success ? m : null); setSummary(s?.success ? s : null); setLoading(false); });
  }, [scanId, apiBase]);

  const providers = mapData?.providers ?? [];
  const facilities = mapData?.facilities ?? [];
  const everyone = [...providers.map((p) => ({ ...p, kind: 'provider' })), ...facilities.map((f) => ({ ...f, kind: 'facility' }))];
  const counties = summary?.counties || [];
  const specialties = summary?.specialties || [];
  const countyName = (code) => counties.find((c) => c.ssa_county_code === code)?.county || code;
  const specName = (code) => specialties.find((s) => s.specialty_code === code)?.specialty || code;
  const shown = everyone.filter((p) => {
    if (!q) return false;
    const t = q.toLowerCase();
    return [p.name, p.npi, p.specialty_code, specName(p.specialty_code), p.city, countyName(p.ssa_state_county_code)].some((v) => String(v || '').toLowerCase().includes(t));
  }).slice(0, 25);
  const removed = everyone.filter((p) => removeIds.includes(p.id));

  const run = () => {
    setRunning(true); setResult(null); setErr('');
    fetch(`${apiBase}/scans/${scanId}/scenario`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        remove_provider_ids: removed.filter((p) => p.kind === 'provider').map((p) => p.id),
        remove_facility_ids: removed.filter((p) => p.kind === 'facility').map((p) => p.id),
        add_virtual: adds.map((a) => ({ ssa_state_county_code: a.county, specialty_code: a.specialty, result_type: a.type, count: a.count })),
      }),
    }).then((r) => r.json()).then((d) => { if (d.success) setResult(d); else setErr(d.message || 'The what if could not run.'); setRunning(false); })
      .catch((e) => { setErr(e.message); setRunning(false); });
  };
  const changed = (result?.projected || []).filter((r) => r.improved || r.worsened);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl border border-slate-200 max-w-3xl w-full max-h-[90vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-1">
          <h3 className="text-lg font-semibold text-slate-800">What if</h3>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-800 text-xl leading-none">×</button>
        </div>
        <p className="text-slate-600 text-sm mb-4">Take providers out of this run, or add providers you are recruiting, and see which standards would change. Counts only; drive time is measured when you run adequacy.</p>
        {loading && <p className="text-slate-500 text-sm">Loading the run.</p>}
        {!loading && (
          <div className="space-y-5">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Take out of the network</div>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a provider or facility by name, NPI, specialty or city" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              {shown.length > 0 && (
                <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                  {shown.map((p) => (
                    <button key={p.id} type="button" onClick={() => { if (!removeIds.includes(p.id)) setRemoveIds([...removeIds, p.id]); setQ(''); }} className="w-full text-left px-3 py-1.5 text-[12.5px] hover:bg-slate-50 flex gap-3">
                      <span className="font-medium text-slate-900 flex-1 truncate">{p.name || 'Unnamed'}</span>
                      <span className="text-slate-500 truncate">{specName(p.specialty_code)}</span>
                      <span className="text-slate-500 truncate">{countyName(p.ssa_state_county_code)}</span>
                    </button>
                  ))}
                </div>
              )}
              {removed.length > 0 && (
                <ul className="mt-2 flex flex-wrap gap-1.5">
                  {removed.map((p) => <li key={p.id} className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 text-[12px] flex items-center gap-1">{p.name || p.npi} · {specName(p.specialty_code)} <button type="button" onClick={() => setRemoveIds(removeIds.filter((x) => x !== p.id))} className="text-rose-500 hover:text-rose-800 leading-none">×</button></li>)}
                </ul>
              )}
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Add to the network</div>
              <div className="flex flex-wrap gap-2 items-end">
                <label className="text-[12px] text-slate-600 flex flex-col">County
                  <select value={draft.county} onChange={(e) => setDraft({ ...draft, county: e.target.value })} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm bg-white min-w-[12rem]">
                    <option value="">Choose a county</option>
                    {counties.map((c) => <option key={c.ssa_county_code} value={c.ssa_county_code}>{c.county}</option>)}
                  </select></label>
                <label className="text-[12px] text-slate-600 flex flex-col">Specialty
                  <select value={draft.specialty} onChange={(e) => setDraft({ ...draft, specialty: e.target.value })} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm bg-white min-w-[14rem]">
                    <option value="">Choose a specialty</option>
                    {specialties.map((s) => <option key={`${s.type}:${s.specialty_code}`} value={`${s.type}:${s.specialty_code}`}>{s.specialty}{s.type === 'facility' ? ' (facility)' : ''}</option>)}
                  </select></label>
                <label className="text-[12px] text-slate-600 flex flex-col">How many
                  <input type="number" min={1} max={500} value={draft.count} onChange={(e) => setDraft({ ...draft, count: e.target.value })} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm w-24" /></label>
                <button type="button" disabled={!draft.county || !draft.specialty || !(parseInt(draft.count, 10) > 0)} onClick={() => { const [type, code] = draft.specialty.split(':'); setAdds([...adds, { county: draft.county, specialty: code, type, count: parseInt(draft.count, 10) }]); setDraft({ county: draft.county, specialty: '', count: 1 }); }} className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold disabled:opacity-40">Add</button>
              </div>
              {adds.length > 0 && (
                <ul className="mt-2 flex flex-wrap gap-1.5">
                  {adds.map((a, i) => <li key={i} className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[12px] flex items-center gap-1">{a.count} {specName(a.specialty)} in {countyName(a.county)} <button type="button" onClick={() => setAdds(adds.filter((_, j) => j !== i))} className="text-emerald-600 hover:text-emerald-900 leading-none">×</button></li>)}
                </ul>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button type="button" onClick={run} disabled={running || (!removed.length && !adds.length)} className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-sm font-semibold disabled:opacity-40">{running ? 'Working' : 'See what changes'}</button>
              {err && <span className="text-xs font-semibold text-rose-600">{err}</span>}
            </div>
            {result && (
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                <div className="text-sm text-slate-800">
                  <span className="text-emerald-700 font-semibold">{result.summary?.improved_count ?? 0} standard{result.summary?.improved_count === 1 ? '' : 's'} would be met</span>
                  <span className="text-slate-400"> · </span>
                  <span className="text-rose-700 font-semibold">{result.summary?.worsened_count ?? 0} would fall below</span>
                  {!changed.length && <span className="text-slate-500"> · nothing changes on counts alone</span>}
                </div>
                {changed.length > 0 && (
                  <table className="w-full text-xs mt-3">
                    <thead><tr className="text-left text-slate-500"><th className="py-1">County</th><th>Specialty</th><th>Now</th><th>After</th></tr></thead>
                    <tbody>
                      {changed.map((r, i) => (
                        <tr key={i} className={r.improved ? 'text-emerald-700' : 'text-rose-700'}>
                          <td className="py-1">{countyName(r.ssa_state_county_code)}</td>
                          <td>{specName(r.specialty_code)}{r.result_type === 'facility' ? ' (facility)' : ''}</td>
                          <td>{r.baseline_actual_count} of {r.required_count}, {r.baseline_status === 'pass' ? 'met' : 'below'}</td>
                          <td>{r.projected_actual_count} of {r.required_count}, {r.projected_status === 'pass' ? 'met' : 'below'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ScansSection({ apiBase, scans, loading, onRefresh, readiness, onRunScan, lastScanNote, lastScanReconciliation, selectedProviderFileIds, selectedFacilityFileIds, onSelectProviderFiles, onSelectFacilityFiles, onShowScanAuditTrail, onShowScanScenario = () => {}, programType = 'medicare_advantage', onSelectProgramType }) {
  const latestScanRecon = lastScanReconciliation || scans?.[0]?.scan_input_summary?.reconciliation || null;
  // Per-scan note shown after a CMS-template download when rows are missing
  // CMS-required fields the roster never carried (never guessed, only counted).
  const [cmsHsdReadiness, setCmsHsdReadiness] = React.useState({});

  const downloadCmsHsd = (scanId, table) => {
    fetch(`${apiBase}/scans/${scanId}/hsd-export?layout=cms&format=xlsx&table=${table}`, { credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error('export failed'); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `scan-${scanId}-cms-${table === 'facilities' ? 'facility' : 'provider'}-hsd.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .then(() => fetch(`${apiBase}/scans/${scanId}/hsd-export/readiness`, { credentials: 'include' }).then((r) => r.json()))
      .then((d) => {
        const r = table === 'facilities' ? d?.facilities : d?.providers;
        if (!r || !r.total_rows) { setCmsHsdReadiness((p) => ({ ...p, [scanId]: '' })); return; }
        // Template marks required columns with a leading asterisk; only those
        // block an upload. Optional Y/N columns may legitimately stay blank.
        const missing = Object.entries(r.blank_counts || {}).filter(([, n]) => n > 0);
        const required = missing.filter(([f]) => f.startsWith('*'));
        setCmsHsdReadiness((p) => ({
          ...p,
          [scanId]: required.length
            ? `CMS file downloaded, but required column(s) have blanks: ${required.map(([f, n]) => `${f.replace(/^\*/, '')} (${n} row${n === 1 ? '' : 's'})`).join('; ')}. Fix the roster before uploading.`
            : 'CMS file downloaded — template-exact, every required column populated. Ready for HPMS.',
        }));
      })
      .catch(() => setCmsHsdReadiness((p) => ({ ...p, [scanId]: 'The CMS-template export failed. Try again.' })));
  };
  const scanReady = readiness?.ready ?? false;
  const providerFiles = readiness?.provider_files ?? [];
  const facilityFiles = readiness?.facility_files ?? [];

  const toggleProvider = (id) => {
    const set = new Set(selectedProviderFileIds);
    if (set.has(id)) set.delete(id); else set.add(id);
    onSelectProviderFiles([...set]);
  };
  const toggleFacility = (id) => {
    const set = new Set(selectedFacilityFileIds);
    if (set.has(id)) set.delete(id); else set.add(id);
    onSelectFacilityFiles([...set]);
  };
  const selectAllProvider = () => onSelectProviderFiles(providerFiles.map((f) => f.id));
  const selectAllFacility = () => onSelectFacilityFiles(facilityFiles.map((f) => f.id));
  const clearProvider = () => onSelectProviderFiles([]);
  const clearFacility = () => onSelectFacilityFiles([]);

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-orange-600 via-orange-500 to-amber-500 rounded-2xl shadow-lg p-5">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-white">Scans / History</h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={programType}
              onChange={(e) => onSelectProgramType?.(e.target.value)}
              className="px-3 py-2 bg-white/95 text-violet-800 rounded-lg text-sm font-medium border border-white/40 focus:outline-none focus:ring-2 focus:ring-white"
              aria-label="Program type"
            >
              <option value="medicare_advantage">Medicare Advantage</option>
              <option value="medicaid">Medicaid Managed Care</option>
              <option value="aca_marketplace">ACA Marketplace</option>
            </select>
            {scanReady && onRunScan && (
              <button
                type="button"
                onClick={() => { onRunScan(); onRefresh(); }}
                disabled={selectedProviderFileIds.length === 0 && selectedFacilityFileIds.length === 0}
                className="px-4 py-2 bg-white text-violet-700 rounded-xl text-sm font-bold hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Run scan
              </button>
            )}
            <button type="button" onClick={onRefresh} className="text-sm text-white/90 hover:text-white">Refresh</button>
          </div>
        </div>
        <p className="text-white/90 text-sm mt-2">Pick the program to evaluate against, choose files, then run a scan. Results appear below.</p>
      </div>
      {scanReady && (
        <div className="bg-white rounded-xl shadow border border-slate-200 p-4">
          <h3 className="text-sm font-bold text-slate-700 mb-3">Select files for this scan</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-600 text-sm font-medium">Provider files</span>
                <div className="flex gap-2">
                  <button type="button" onClick={selectAllProvider} className="text-xs text-violet-600 hover:underline">All</button>
                  <button type="button" onClick={clearProvider} className="text-xs text-slate-500 hover:underline">None</button>
                </div>
              </div>
              <ul className="space-y-1.5 max-h-40 overflow-y-auto border border-slate-100 rounded-lg p-2 bg-slate-50/50">
                {providerFiles.length === 0 ? (
                  <li className="text-slate-500 text-sm">No provider files</li>
                ) : (
                  providerFiles.map((f) => (
                    <li key={f.id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`prov-${f.id}`}
                        checked={selectedProviderFileIds.includes(f.id)}
                        onChange={() => toggleProvider(f.id)}
                        className="rounded border-slate-300 text-violet-600"
                      />
                      <label htmlFor={`prov-${f.id}`} className="text-sm text-slate-700 cursor-pointer truncate" title={f.original_filename}>
                        {f.batch_label ? `${f.batch_label} — ` : ''}{f.original_filename}
                      </label>
                    </li>
                  ))
                )}
              </ul>
              <p className="text-slate-500 text-xs mt-1">{selectedProviderFileIds.length} of {providerFiles.length} selected</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-600 text-sm font-medium">Facility files</span>
                <div className="flex gap-2">
                  <button type="button" onClick={selectAllFacility} className="text-xs text-violet-600 hover:underline">All</button>
                  <button type="button" onClick={clearFacility} className="text-xs text-slate-500 hover:underline">None</button>
                </div>
              </div>
              <ul className="space-y-1.5 max-h-40 overflow-y-auto border border-slate-100 rounded-lg p-2 bg-slate-50/50">
                {facilityFiles.length === 0 ? (
                  <li className="text-slate-500 text-sm">No facility files</li>
                ) : (
                  facilityFiles.map((f) => (
                    <li key={f.id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`fac-${f.id}`}
                        checked={selectedFacilityFileIds.includes(f.id)}
                        onChange={() => toggleFacility(f.id)}
                        className="rounded border-slate-300 text-violet-600"
                      />
                      <label htmlFor={`fac-${f.id}`} className="text-sm text-slate-700 cursor-pointer truncate" title={f.original_filename}>
                        {f.batch_label ? `${f.batch_label} — ` : ''}{f.original_filename}
                      </label>
                    </li>
                  ))
                )}
              </ul>
              <p className="text-slate-500 text-xs mt-1">{selectedFacilityFileIds.length} of {facilityFiles.length} selected</p>
            </div>
          </div>
        </div>
      )}
      {scanReady && onRunScan && (
        <div className="space-y-2">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-800">
            <strong>Next scan will use:</strong> {selectedProviderFileIds.length} provider file(s), {selectedFacilityFileIds.length} facility file(s). Different selection = different results. Select files above, then click <strong>Run scan</strong>. New scans appear in the table below. Re-running with the same data updates existing findings.
          </div>
          {readiness?.population_advisory && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
              <span><strong>Coverage method:</strong> {readiness.population_advisory}</span>
            </div>
          )}
          {lastScanNote && (
            <div className="bg-slate-100 border border-slate-200 rounded-xl px-4 py-2 text-xs text-slate-700 font-mono">
              Last scan: {lastScanNote}
            </div>
          )}
          {latestScanRecon && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-2 text-xs text-indigo-900">
              reconciliation: evaluated={latestScanRecon.evaluated_results_count ?? 0}, fail={latestScanRecon.fail_results_count ?? 0}, fragile={latestScanRecon.fragile_results_count ?? 0}, findings_linked={latestScanRecon.findings_linked_count ?? 0}, service_area={latestScanRecon.service_area_count ?? 0}{latestScanRecon.service_area_fallback_used ? ' (fallback)' : ''}
            </div>
          )}
        </div>
      )}
      {!scanReady && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800">
          Upload at least one provider or facility file in the <strong>Uploads</strong> tab, then return here to run a scan.
        </div>
      )}
      {loading ? (
        <div className="text-slate-500">Loading scans…</div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-2xl shadow border border-slate-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-600 border-b border-slate-200 bg-slate-50/80">
                <th className="pb-3 pt-3 pl-4 pr-4 font-semibold">Scan time</th>
                <th className="pb-3 pt-3 pr-4 font-semibold">Program</th>
                <th className="pb-3 pt-3 pr-4 font-semibold">Input</th>
                <th className="pb-3 pt-3 pr-4 font-semibold">Overall</th>
                <th className="pb-3 pt-3 pr-4 font-semibold">Directory accuracy</th>
                <th className="pb-3 pt-3 pr-4 font-semibold">Status</th>
                <th className="pb-3 pt-3 pr-4 font-semibold">Fail / Findings</th>
                <th className="pb-3 pt-3 pr-4 font-semibold">Export</th>
              </tr>
            </thead>
            <tbody>
              {scans.map((s) => (
                <tr key={s.id} className="border-b border-slate-100">
                  <td className="py-3 pl-4 pr-4 text-slate-800">{new Date(s.scan_timestamp).toLocaleString()}</td>
                  <td className="py-3 pr-4 text-slate-700 text-xs">{PROGRAM_LABELS[s.program_type] || s.program_type || 'Medicare Advantage'}</td>
                  <td className="py-3 pr-4 text-slate-600 text-xs" title="Provider files, facility files, service-area counties">{s._scan_input_label || `${s.provider_upload_file_ids?.length ?? 0} prov, ${s.facility_upload_file_ids?.length ?? 0} fac`}</td>
                  <td className={`py-3 pr-4 font-semibold tabular-nums ${pctColor(s.adequacy_pct_unweighted)}`}>{formatPct(s.adequacy_pct_unweighted)}</td>
                  <td className={`py-3 pr-4 font-semibold tabular-nums ${pctColor(s.directory_accuracy_pct)}`}>{formatPct(s.directory_accuracy_pct)}</td>
                  <td className="py-3 pr-4 text-slate-600">{s.status}</td>
                  <td className="py-3 pr-4 text-slate-600">{s.fail_count} / {s.finding_count}</td>
                  <td className="py-3 pr-4 flex flex-wrap gap-2">
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        fetch(`${apiBase}/scans/${s.id}/results?format=csv`, { credentials: 'include' })
                          .then((r) => r.text())
                          .then((csv) => {
                            const blob = new Blob([csv], { type: 'text/csv' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `scan-${s.id}-results.csv`;
                            a.click();
                            URL.revokeObjectURL(url);
                          });
                      }}
                      className="text-violet-600 hover:text-violet-800 text-xs font-medium"
                    >
                      Results CSV
                    </a>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        fetch(`${apiBase}/scans/${s.id}/hsd-export?format=csv&table=providers`, { credentials: 'include' })
                          .then((r) => r.text())
                          .then((csv) => {
                            const blob = new Blob([csv], { type: 'text/csv' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `scan-${s.id}-hsd-providers.csv`;
                            a.click();
                            URL.revokeObjectURL(url);
                          });
                      }}
                      className="text-violet-600 hover:text-violet-800 text-xs font-medium"
                    >
                      HSD Providers
                    </a>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        fetch(`${apiBase}/scans/${s.id}/hsd-export?format=csv&table=facilities`, { credentials: 'include' })
                          .then((r) => r.text())
                          .then((csv) => {
                            const blob = new Blob([csv], { type: 'text/csv' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `scan-${s.id}-hsd-facilities.csv`;
                            a.click();
                            URL.revokeObjectURL(url);
                          });
                      }}
                      className="text-violet-600 hover:text-violet-800 text-xs font-medium"
                    >
                      HSD Facilities
                    </a>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        fetch(`${apiBase}/scans/${s.id}/hsd-export?format=xlsx`, { credentials: 'include' })
                          .then((r) => r.blob())
                          .then((blob) => {
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `scan-${s.id}-hsd-export.xlsx`;
                            a.click();
                            URL.revokeObjectURL(url);
                          });
                      }}
                      className="text-violet-600 hover:text-violet-800 text-xs font-medium"
                    >
                      HSD Excel
                    </a>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        const a0 = e.currentTarget; a0.textContent = 'Building…';
                        fetch(`${apiBase}/scans/${s.id}/adequacy-report`, { credentials: 'include' })
                          .then((r) => { if (!r.ok) throw new Error('report failed'); return r.blob(); })
                          .then((blob) => {
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `Network-Adequacy-Report-${s.id.slice(0, 8)}.pdf`;
                            a.click();
                            URL.revokeObjectURL(url);
                          })
                          .catch(() => alert('Could not generate the report.'))
                          .finally(() => { a0.textContent = 'PDF report'; });
                      }}
                      className="text-orange-600 hover:text-orange-800 text-xs font-semibold"
                      title="Client-facing adequacy report (PDF) with CMS exception outlook"
                    >
                      PDF report
                    </a>
                    <a
                      href="#"
                      onClick={(e) => { e.preventDefault(); downloadCmsHsd(s.id, 'providers'); }}
                      className="text-emerald-700 hover:text-emerald-900 text-xs font-semibold"
                      title="Exact CMS Provider HSD Table layout — upload-ready for HPMS/NMM"
                    >
                      CMS Provider Table
                    </a>
                    <a
                      href="#"
                      onClick={(e) => { e.preventDefault(); downloadCmsHsd(s.id, 'facilities'); }}
                      className="text-emerald-700 hover:text-emerald-900 text-xs font-semibold"
                      title="Exact CMS Facility HSD Table layout — upload-ready for HPMS/NMM"
                    >
                      CMS Facility Table
                    </a>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        const a0 = e.currentTarget; a0.textContent = 'Building…';
                        fetch(`${apiBase}/scans/${s.id}/ncqa-evidence-pack`, { credentials: 'include' })
                          .then((r) => { if (!r.ok) throw new Error('pack failed'); return r.blob(); })
                          .then((blob) => {
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `NCQA-Network-Evidence-Pack-${s.id.slice(0, 8)}.pdf`;
                            a.click();
                            URL.revokeObjectURL(url);
                          })
                          .catch(() => alert('Could not generate the evidence pack.'))
                          .finally(() => { a0.textContent = 'NCQA pack'; });
                      }}
                      className="text-sky-700 hover:text-sky-900 text-xs font-semibold"
                      title="Accreditation evidence pack (PDF): availability, geographic access, behavioral health, ghost detection, monitoring"
                    >
                      NCQA pack
                    </a>
                    {cmsHsdReadiness[s.id] && (
                      <span className="w-full text-[11px] text-amber-700">{cmsHsdReadiness[s.id]}</span>
                    )}
                    <a href={`${API}/api/na-roster/adequacy/scans/${s.id}/acc-report.xlsx`} className="text-violet-600 hover:text-violet-800 text-xs font-medium" title="The accuracy report in the HPMS automated criteria check layout: required, submitted, within drive time, beds, threshold, credits, pass or fail, and why.">ACC report</a>
                    <a href={`${API}/api/na-roster/adequacy/scans/${s.id}/hpms-provider.zip`} className="text-violet-600 hover:text-violet-800 text-xs font-medium" title="The provider table this run measured, as HPMS takes it: a zip holding one tab delimited text file.">HPMS provider file</a>
                    <a href={`${API}/api/na-roster/adequacy/scans/${s.id}/hpms-facility.zip`} className="text-violet-600 hover:text-violet-800 text-xs font-medium" title="The facility table this run measured, as HPMS takes it.">HPMS facility file</a>
                    <a
                      href="#"
                      onClick={(e) => { e.preventDefault(); onShowScanScenario?.(s.id); }}
                      className="text-violet-600 hover:text-violet-800 text-xs font-medium"
                    >
                      What if
                    </a>
                    <a
                      href="#"
                      onClick={(e) => { e.preventDefault(); onShowScanAuditTrail?.(s.id); }}
                      className="text-violet-600 hover:text-violet-800 text-xs font-medium"
                    >
                      Audit trail
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {scans.length === 0 && <p className="text-slate-500 py-6 px-4">No scans yet.</p>}
        </div>
      )}
    </div>
  );
}

function DraftsSection({ drafts, loading, onRefresh, apiBase, selectedDraftId, onSelectDraft }) {
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState(null);
  const [supportRows, setSupportRows] = useState([]);
  const [supportRowsLoading, setSupportRowsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addRowOpen, setAddRowOpen] = useState(false);
  const [newRow, setNewRow] = useState({ row_type: 'non_contracted_provider', provider_or_facility_name: '', npi: '', address_line: '', city: '', state: '', zip: '', specialty_code: '', ssa_county_code: '', reason: '', phone: '', notes: '' });
  const [addingRow, setAddingRow] = useState(false);

  const selectedDraft = selectedDraftId ? drafts.find((d) => d.id === selectedDraftId) : null;

  useEffect(() => {
    if (!selectedDraftId || !apiBase) {
      setSupportRows([]);
      return;
    }
    setSupportRowsLoading(true);
    fetch(`${apiBase}/exception-drafts/${selectedDraftId}/support-rows`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => (d.success && d.support_rows ? setSupportRows(d.support_rows) : setSupportRows([])))
      .catch(() => setSupportRows([]))
      .finally(() => setSupportRowsLoading(false));
  }, [selectedDraftId, apiBase]);

  const createDraft = () => {
    if (creating) return;
    setCreating(true);
    setMessage(null);
    fetch(`${apiBase}/exception-drafts`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          setMessage('Draft created. Open it below to edit and export.');
          onRefresh();
          if (d.draft?.id && onSelectDraft) onSelectDraft(d.draft.id);
        } else {
          setMessage(d.message || 'Failed to create draft');
        }
        setCreating(false);
      })
      .catch((e) => {
        setMessage(e.message || 'Failed to create draft');
        setCreating(false);
      });
  };

  const [cmsMessage, setCmsMessage] = useState('');
  const handleExportCms = () => {
    if (!selectedDraftId || !apiBase) return;
    setCmsMessage('Building the CMS form.');
    fetch(`${apiBase}/exception-drafts/${selectedDraftId}/export?format=cms`, { credentials: 'include' })
      .then(async (r) => { if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.message || 'The CMS form could not be built.'); } const name = (r.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/)?.[1] || 'exception.zip'; return r.blob().then((b) => ({ b, name })); })
      .then(({ b, name }) => { const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = name; a.click(); URL.revokeObjectURL(a.href); setCmsMessage(`Downloaded ${name}. Upload it in HPMS under Exception Upload.`); })
      .catch((e) => setCmsMessage(e.message));
  };
  const handleSaveDraft = (rationale_section, sources_consulted, narrative_section, cms_form) => {
    if (!selectedDraftId || saving) return;
    setSaving(true);
    fetch(`${apiBase}/exception-drafts/${selectedDraftId}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rationale_section, sources_consulted, narrative_section, cms_form })
    })
      .then((r) => r.json())
      .then((d) => { if (d.success) onRefresh(); setSaving(false); })
      .catch(() => setSaving(false));
  };

  const handleExportDocx = () => {
    if (!selectedDraftId || !apiBase) return;
    fetch(`${apiBase}/exception-drafts/${selectedDraftId}/export?format=docx`, { credentials: 'include' })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `exception-packet-${selectedDraftId.slice(0, 8)}.docx`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => {});
  };

  const handleAddSupportRow = (e) => {
    e.preventDefault();
    if (!selectedDraftId || addingRow) return;
    setAddingRow(true);
    fetch(`${apiBase}/exception-drafts/${selectedDraftId}/support-rows`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newRow)
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          setSupportRows((prev) => [...prev, { ...newRow, id: d.support_row?.id || String(Date.now()) }]);
          setNewRow({ row_type: 'non_contracted_provider', provider_or_facility_name: '', npi: '', address_line: '', city: '', state: '', zip: '', specialty_code: '', ssa_county_code: '', reason: '', phone: '', notes: '' });
          setAddRowOpen(false);
        }
        setAddingRow(false);
      })
      .catch(() => setAddingRow(false));
  };

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-orange-600 via-orange-500 to-amber-500 rounded-2xl shadow-lg p-5">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-lg font-bold text-white">Exception drafts</h2>
              <p className="text-white/90 text-sm mt-0.5">Each request is the CMS exception form, filled here and downloaded ready for HPMS. Start one from the finding it answers.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={createDraft} disabled={creating} className="px-4 py-2 bg-white/20 hover:bg-white/30 disabled:opacity-50 text-white rounded-xl text-sm font-medium">
              {creating ? 'Creating…' : 'New draft'}
            </button>
            <button type="button" onClick={onRefresh} className="text-sm text-white/90 hover:text-white">Refresh</button>
          </div>
        </div>
      </div>
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 text-sm text-slate-700">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">What CMS accepts</h4>
        <p className="mb-1">CMS grants an exception when the providers it lists as available cannot serve the plan and the plan has contracted the closest available providers, consistent with the local pattern of care. Reasons it accepts for a nearby provider not contracted: no longer practicing, not at that address, not that specialty, opted out of Medicare, contracts with no Medicare Advantage organization, excluded. Inability to contract and ongoing negotiation are not accepted.</p>
      </div>
      {message && (
        <div className={`px-4 py-2 rounded-xl text-sm ${message.startsWith('Draft created') ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
          {message}
        </div>
      )}
      {loading ? (
        <div className="text-slate-500">Loading drafts…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-1">
            <h3 className="text-sm font-semibold text-slate-800 mb-2">Drafts</h3>
            <ul className="space-y-2">
              {drafts.length === 0 ? (
                <li className="text-slate-500 py-6 px-4 bg-white rounded-xl shadow border border-slate-200 text-center text-sm">
                  No exception drafts yet. Click <strong>New draft</strong> above or create one from a finding in the Findings tab.
                </li>
              ) : (
                drafts.map((d) => (
                  <li
                    key={d.id}
                    onClick={() => onSelectDraft?.(d.id)}
                    className={`py-3 px-4 rounded-xl border cursor-pointer transition-colors ${selectedDraftId === d.id ? 'bg-violet-100 border-violet-300' : 'bg-white shadow border-slate-200 hover:bg-slate-50'}`}
                  >
                    <span className="text-slate-800 font-medium block truncate text-sm">{d.finding_title || (d.finding_id ? 'Linked to a finding' : 'Not linked to a finding')}</span>
                    <span className="text-slate-500 text-xs">{(d.review_state ?? 'draft')} · {new Date(d.updated_at).toLocaleString()}</span>
                  </li>
                ))
              )}
            </ul>
          </div>
          <div className="lg:col-span-2">
            {selectedDraft ? (
              <DraftDetailPanel
                draft={selectedDraft}
                supportRows={supportRows}
                supportRowsLoading={supportRowsLoading}
                onClose={() => onSelectDraft?.(null)}
                onSave={handleSaveDraft}
                onExportDocx={handleExportDocx}
                onExportCms={handleExportCms}
                cmsMessage={cmsMessage}
                saving={saving}
                addRowOpen={addRowOpen}
                setAddRowOpen={setAddRowOpen}
                newRow={newRow}
                setNewRow={setNewRow}
                onAddSupportRow={handleAddSupportRow}
                addingRow={addingRow}
              />
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-6 text-center text-slate-500 text-sm">
                Choose a request on the left, or start one from a finding on the Findings tab.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// The CMS exception form (OMB 0938-1346), field for field, in plain words.
const CMS_PART2 = [
  ['q1', 'Did you review public databases and other sources for providers in this county and specialty?'],
  ['q2', 'Did that review find providers within the CMS criteria that you have not contracted?'],
  ['q3', 'Did you contract with providers outside the CMS criteria?'],
  ['q4', 'Are there non contracted providers outside the criteria who are closer to enrollees than your contracted providers?'],
];
const CMS_SOURCES = ['Physician Compare', 'Hospital Compare', 'Nursing Home Compare', 'Dialysis Compare', 'NPI file/NPPES', 'Provider of Services (POS) file', 'Direct outreach to provider', 'Provider website', 'State licensing data', 'Online mapping tool', 'Other (Please describe in Part IV)'];
const CMS_REASONS = [
  'Provider is no longer practicing (e.g., deceased, retired, etc.)',
  'Provider does not provide services at the office/facility address listed in database',
  'Provider does not provide services in the specialty type listed in the database and for which this exception is being requested',
  'Provider has opted out of Medicare',
  'Provider does not contract with any Medicare Advantage Organizations',
  'Sanctioned provider on List of Excluded Individuals and Entities',
  'Provider is at capacity and is not accepting new patients',
  'Other (Note to MAOs: Please provide an explanation in the "Additional Notes on Reason for Not Contracting" field)',
];
const REASON_SHORT = (r) => String(r || '').replace(/\s*\(.*$/, '').replace(' listed in the database and for which this exception is being requested', '');

function DraftDetailPanel({ draft, supportRows, supportRowsLoading, onClose, onSave, onExportDocx, onExportCms, cmsMessage, saving, addRowOpen, setAddRowOpen, newRow, setNewRow, onAddSupportRow, addingRow }) {
  const [rationale, setRationale] = useState(draft.rationale_section ?? '');
  const [sources, setSources] = useState(draft.sources_consulted ?? '');
  const [narrative, setNarrative] = useState(draft.narrative_section ?? '');
  const [cms, setCms] = useState({ contract_id: '', q1: '', q2: '', q3: '', q4: '', sources: [], ...(draft.cms_form || {}) });

  useEffect(() => {
    setRationale(draft.rationale_section ?? '');
    setSources(draft.sources_consulted ?? '');
    setNarrative(draft.narrative_section ?? '');
    setCms({ contract_id: '', q1: '', q2: '', q3: '', q4: '', sources: [], ...(draft.cms_form || {}) });
  }, [draft.id, draft.rationale_section, draft.sources_consulted, draft.narrative_section, draft.cms_form]);

  const setSource = (i, v) => { const s = [...(cms.sources || [])]; s[i] = v; setCms({ ...cms, sources: s.filter((x, j) => x || j < i) }); };
  const label = 'block text-xs font-semibold text-slate-600 mb-1';
  const input = 'w-full rounded-lg border border-slate-200 p-2 text-sm text-slate-800 bg-white';

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50/50">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">{draft.finding_title || 'Exception request'}</h3>
          <p className="text-[11px] text-slate-500">The fields below are the CMS exception form. Fill them here, then download the form filled and zipped for HPMS.</p>
        </div>
        <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-800 text-sm">Close</button>
      </div>
      <div className="p-4 space-y-4">
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className={label}>Contract ID</label>
            <input value={cms.contract_id || ''} onChange={(e) => setCms({ ...cms, contract_id: e.target.value.toUpperCase() })} placeholder="H1234" className={input} />
            <p className="text-[11px] text-slate-500 mt-1">CMS names the form ContractID_CountyCode_SpecialtyCode.</p>
          </div>
          <div className="sm:col-span-2 text-[12px] text-slate-600 self-end">
            {draft.finding_id ? <span>County and specialty come from the finding this request is linked to.</span> : <span className="text-amber-700">This draft is not linked to a finding. Start exception requests from a finding so the county and specialty are known.</span>}
          </div>
        </div>
        <div>
          <label className={label}>Part II. About the search for providers</label>
          <div className="space-y-1.5">
            {CMS_PART2.map(([k, text]) => (
              <div key={k} className="flex items-center gap-3 text-[12.5px] text-slate-800">
                <select value={cms[k] || ''} onChange={(e) => setCms({ ...cms, [k]: e.target.value })} className="rounded-lg border border-slate-200 px-2 py-1 text-sm bg-white w-24"><option value="">Choose</option><option>Yes</option><option>No</option></select>
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <label className={label}>Part III. Sources checked (up to five)</label>
          <div className="grid sm:grid-cols-2 gap-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <select key={i} value={(cms.sources || [])[i] || ''} onChange={(e) => setSource(i, e.target.value)} className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm bg-white">
                <option value="">Source {i + 1}</option>
                {CMS_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            ))}
          </div>
        </div>
        <div>
          <label className={label}>Part IV. Justification</label>
          <p className="text-xs text-slate-500 mb-1">Why the providers CMS lists cannot serve the plan, and how the contracted network follows the local pattern of care. Reasons CMS accepts: provider no longer practicing, not at that address, not that specialty, opted out of Medicare, contracts with no one, excluded.</p>
          <textarea value={rationale} onChange={(e) => setRationale(e.target.value)} rows={4} className={input} placeholder="Write the justification here." />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className={label}>How access is maintained meanwhile</label>
            <textarea value={narrative} onChange={(e) => setNarrative(e.target.value)} rows={3} className={input} placeholder="Telehealth, recruitment under way, out of network arrangements at in network cost sharing." />
          </div>
          <div>
            <label className={label}>Notes on sources (optional)</label>
            <textarea value={sources} onChange={(e) => setSources(e.target.value)} rows={3} className={input} placeholder="Dates checked, files used, anything a reviewer should know." />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <button type="button" onClick={() => onSave(rationale, sources, narrative, cms)} disabled={saving} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white rounded-xl text-sm font-semibold">{saving ? 'Saving' : 'Save'}</button>
          <button type="button" onClick={onExportCms} className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-sm font-semibold" title="The CMS exception form filled from this draft, zipped as HPMS takes it. Save first.">CMS exception form for HPMS (.zip)</button>
          <button type="button" onClick={onExportDocx} className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-xl text-sm font-medium">Word summary</button>
          {cmsMessage && <span className={`text-xs font-semibold ${/could not|Enter|not linked/i.test(cmsMessage) ? 'text-rose-600' : 'text-emerald-700'}`}>{cmsMessage}</span>}
        </div>
        <div className="pt-4 border-t border-slate-200">
          <h4 className="text-xs font-semibold text-slate-700 mb-1">Part V. Providers nearby that you did not contract, and why</h4>
          <p className="text-[11px] text-slate-500 mb-2">One row per provider CMS could say you should have contracted. The first 94 go on the main form; the rest go on the continuation form, which downloads in the same zip.</p>
          {supportRowsLoading ? <p className="text-slate-500 text-sm">Loading.</p> : (
            <>
              {supportRows.length === 0 ? <p className="text-slate-500 text-sm mb-2">No providers listed yet.</p> : (
                <table className="w-full text-[12px] mb-2">
                  <thead><tr className="text-left text-slate-500"><th className="py-1">Provider</th><th>NPI</th><th>Where</th><th>Reason not contracted</th></tr></thead>
                  <tbody>
                    {supportRows.map((r) => (
                      <tr key={r.id} className="border-t border-slate-100">
                        <td className="py-1 font-medium text-slate-900">{r.provider_or_facility_name || 'Unnamed'}</td>
                        <td className="text-slate-600">{r.npi || ''}</td>
                        <td className="text-slate-600">{[r.city, r.state].filter(Boolean).join(', ')}</td>
                        <td className="text-slate-600">{REASON_SHORT(r.reason) || <span className="text-amber-700">no reason yet</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {!addRowOpen ? (
                <button type="button" onClick={() => setAddRowOpen(true)} className="text-sm text-orange-700 hover:underline font-semibold">Add a provider</button>
              ) : (
                <form onSubmit={onAddSupportRow} className="bg-slate-50 rounded-lg p-3 space-y-2 text-sm">
                  <div className="grid sm:grid-cols-3 gap-2">
                    <input value={newRow.provider_or_facility_name} onChange={(e) => setNewRow({ ...newRow, provider_or_facility_name: e.target.value })} className="rounded border border-slate-200 px-2 py-1 sm:col-span-2" placeholder="Provider or facility name" required />
                    <input value={newRow.npi} onChange={(e) => setNewRow({ ...newRow, npi: e.target.value })} className="rounded border border-slate-200 px-2 py-1" placeholder="NPI (10 digits)" />
                    <input value={newRow.address_line} onChange={(e) => setNewRow({ ...newRow, address_line: e.target.value })} className="rounded border border-slate-200 px-2 py-1 sm:col-span-2" placeholder="Street address" />
                    <input value={newRow.phone} onChange={(e) => setNewRow({ ...newRow, phone: e.target.value })} className="rounded border border-slate-200 px-2 py-1" placeholder="Phone" />
                    <input value={newRow.city} onChange={(e) => setNewRow({ ...newRow, city: e.target.value })} className="rounded border border-slate-200 px-2 py-1" placeholder="City" />
                    <input value={newRow.state} onChange={(e) => setNewRow({ ...newRow, state: e.target.value.toUpperCase().slice(0, 2) })} className="rounded border border-slate-200 px-2 py-1" placeholder="State" />
                    <input value={newRow.zip} onChange={(e) => setNewRow({ ...newRow, zip: e.target.value })} className="rounded border border-slate-200 px-2 py-1" placeholder="ZIP" />
                    <select value={newRow.reason} onChange={(e) => setNewRow({ ...newRow, reason: e.target.value })} className="rounded border border-slate-200 px-2 py-1 sm:col-span-3 bg-white" required>
                      <option value="">Why this provider was not contracted (the CMS list)</option>
                      {CMS_REASONS.map((r) => <option key={r} value={r}>{REASON_SHORT(r)}</option>)}
                    </select>
                    <input value={newRow.notes} onChange={(e) => setNewRow({ ...newRow, notes: e.target.value })} className="rounded border border-slate-200 px-2 py-1 sm:col-span-3" placeholder="Notes (required when the reason is Other)" />
                  </div>
                  <div className="flex gap-2">
                    <button type="submit" disabled={addingRow} className="px-3 py-1.5 bg-slate-900 text-white rounded-lg text-sm disabled:opacity-50">{addingRow ? 'Adding' : 'Add'}</button>
                    <button type="button" onClick={() => setAddRowOpen(false)} className="px-3 py-1.5 bg-slate-200 text-slate-700 rounded-lg text-sm">Cancel</button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SettingsSection({ data, loading, onRefresh, apiBase, populationWeightedZipCount, onRefreshReadiness }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const [censusLoading, setCensusLoading] = useState(false);
  const [censusResult, setCensusResult] = useState(null);
  const [tableText, setTableText] = useState('');
  const [tableFile, setTableFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzedRows, setAnalyzedRows] = useState(null);
  const [applyLoading, setApplyLoading] = useState(false);
  const [hsdFile, setHsdFile] = useState(null);
  const [hsdUploading, setHsdUploading] = useState(false);
  const [lastHsdResult, setLastHsdResult] = useState(null);
  const [lastHsdFilename, setLastHsdFilename] = useState(null);
  const [countyRefCount, setCountyRefCount] = useState(null);
  const [countyRefFromHsd, setCountyRefFromHsd] = useState(null);
  const [ingestionCountRows, setIngestionCountRows] = useState([]);
  const [ingestionTDRows, setIngestionTDRows] = useState([]);
  const [ingestionLoading, setIngestionLoading] = useState(false);
  const [ingestionSummary, setIngestionSummary] = useState(null);
  const [hsdWarningsDetail, setHsdWarningsDetail] = useState(null);
  const [hsdWarningsLoading, setHsdWarningsLoading] = useState(false);
  const [hsdWarningsExpanded, setHsdWarningsExpanded] = useState(false);
  const [conStates, setConStates] = useState([]);
  const [conStatesLoading, setConStatesLoading] = useState(false);
  const [conStateFips, setConStateFips] = useState('');
  const [conStateSaving, setConStateSaving] = useState(false);

  const fetchCountyRef = useCallback(() => {
    fetch(`${apiBase}/reference/counties`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (d.success && Array.isArray(d.counties)) {
          setCountyRefCount((d.counties || []).length);
          setCountyRefFromHsd((d.counties || []).filter((c) => c.reference_source === 'HSD reference file').length);
        }
      })
      .catch(() => {});
  }, [apiBase]);

  useEffect(() => {
    fetchCountyRef();
  }, [fetchCountyRef]);

  const fetchConStates = useCallback(() => {
    setConStatesLoading(true);
    fetch(`${apiBase}/settings/con-states`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (d.success && Array.isArray(d.states)) setConStates(d.states);
        setConStatesLoading(false);
      })
      .catch(() => setConStatesLoading(false));
  }, [apiBase]);

  useEffect(() => {
    fetchConStates();
  }, [fetchConStates]);

  const fetchRawIngestion = useCallback(() => {
    setIngestionLoading(true);
    Promise.all([
      fetch(`${apiBase}/ingestion/count`, { credentials: 'include' }).then((r) => r.json()),
      fetch(`${apiBase}/ingestion/time-distance`, { credentials: 'include' }).then((r) => r.json())
    ])
      .then(([countData, tdData]) => {
        const countRows = Array.isArray(countData?.rows) ? countData.rows : [];
        const tdRows = Array.isArray(tdData?.rows) ? tdData.rows : [];
        setIngestionCountRows(countRows);
        setIngestionTDRows(tdRows);
        setIngestionSummary((prev) => {
          const fromRows = [...countRows, ...tdRows].find((r) => r.batch_id);
          return {
            success: prev?.success ?? true,
            status: prev?.status || 'success',
            batch_id: prev?.batch_id ?? fromRows?.batch_id ?? null,
            timestamp: prev?.timestamp ?? fromRows?.uploaded_at ?? null,
            count_rows: countRows.length,
            td_rows: tdRows.length,
            validationFailures: Array.isArray(prev?.validationFailures) ? prev.validationFailures : [],
            warningsSummary: prev?.warningsSummary ?? null,
            warningCounts: prev?.warningCounts ?? null,
            warningsSample: prev?.warningsSample ?? null
          };
        });
        setIngestionLoading(false);
      })
      .catch(() => {
        setIngestionLoading(false);
      });
  }, [apiBase]);

  useEffect(() => {
    fetchRawIngestion();
  }, [fetchRawIngestion]);

  const requirements = data?.requirements ?? [];
  const summary = data?.summary ?? { total: 0, by_source: {} };

  const handleUpload = (e) => {
    e.preventDefault();
    if (!file) { setMessage('Select a file first'); return; }
    setUploading(true);
    setMessage(null);
    const formData = new FormData();
    formData.append('file', file);
    fetch(`${apiBase}/settings/cms-minimums`, { method: 'POST', credentials: 'include', body: formData })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          setMessage(`Updated ${d.updated} requirement row(s). Scans will use these minimums.`);
          setFile(null);
          if (e.target?.querySelector?.('input[type="file"]')) e.target.querySelector('input[type="file"]').value = '';
          onRefresh();
        } else setMessage(d.message || 'Upload failed');
        setUploading(false);
      })
      .catch((err) => { setMessage(err.message || 'Upload failed'); setUploading(false); });
  };

  const handleAnalyzeTable = () => {
    if (!tableText.trim() && !tableFile) { setMessage('Paste the table or upload a file.'); return; }
    const formData = new FormData();
    if (tableText.trim()) formData.append('tableText', tableText.trim());
    if (tableFile) formData.append('table', tableFile);
    setAnalyzing(true);
    setMessage(null);
    setAnalyzedRows(null);
    fetch(`${apiBase}/settings/cms-minimums/analyze-table`, { method: 'POST', credentials: 'include', body: formData })
      .then((r) => r.json())
      .then((d) => {
        setAnalyzing(false);
        if (d.success && Array.isArray(d.rows) && d.rows.length > 0) {
          setAnalyzedRows(d.rows);
          setMessage(`Extracted ${d.rows.length} row(s). Review below and click Apply to use them.`);
        } else setMessage(d.message || 'No rows extracted.');
      })
      .catch((err) => { setAnalyzing(false); setMessage(err.message || 'Analysis failed'); });
  };

  const handleApplyAnalyzed = () => {
    if (!analyzedRows || analyzedRows.length === 0) return;
    setApplyLoading(true);
    setMessage(null);
    fetch(`${apiBase}/settings/cms-minimums/apply-analyzed`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rows: analyzedRows })
    })
      .then((r) => {
        if (!r.ok) throw new Error(r.statusText || 'Apply failed');
        return r.json();
      })
      .then((d) => {
        setApplyLoading(false);
        if (d.success) {
          setMessage(`Applied ${d.updated} minimums. Scans will use these.`);
          setAnalyzedRows(null);
          setTableText('');
          setTableFile(null);
          onRefresh();
          setTimeout(() => onRefresh(), 300);
        } else setMessage(d.message || 'Apply failed');
      })
      .catch((err) => { setApplyLoading(false); setMessage(err.message || 'Apply failed'); });
  };

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-orange-600 via-orange-500 to-amber-500 rounded-2xl shadow-lg p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-lg font-bold text-white">Settings</h2>
              <p className="text-white/90 text-sm mt-0.5">Upload the official CMS PDF to load minimums. Scans use this table.</p>
            </div>
          </div>
          <button type="button" onClick={onRefresh} className="text-sm text-white/90 hover:text-white">Refresh</button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow border border-slate-200 p-5">
        <h3 className="text-base font-bold text-slate-800 mb-2">Ingest HSD Reference File</h3>
        <p className="text-slate-600 text-sm mb-4">
          <strong>Upload the official CMS HSD Reference Excel</strong> (e.g. <code className="bg-slate-100 px-1 rounded">ma_reference_file_12-17-2025.xlsx</code> from HPMS). We ingest: <strong>Minimum Provider #s</strong> and <strong>Minimum Facility #s</strong> (min counts by county type), and <strong>Provider Time &amp; Distance</strong> and <strong>Facility Time &amp; Distance</strong> (max minutes and miles). The DB is updated as soon as you click the button—no separate apply step. No AI.
        </p>
        <form onSubmit={(e) => {
          e.preventDefault();
          if (!hsdFile) { setMessage('Select the HSD Reference Excel file first.'); return; }
          setHsdUploading(true);
          setMessage(null);
          const formData = new FormData();
          formData.append('file', hsdFile);
          const filename = hsdFile.name || null;
          fetch(`${apiBase}/settings/cms-minimums/upload-hsd-reference`, { method: 'POST', credentials: 'include', body: formData })
            .then(async (r) => {
              const contentType = (r.headers.get('content-type') || '').toLowerCase();
              const isJson = contentType.includes('application/json');
              const body = await (isJson ? r.json() : r.text());
              const data = isJson ? body : null;
              if (!isJson && body) {
                console.error('HSD upload: non-JSON response', r.status, body.slice(0, 200));
              }
              return { ok: r.ok, data: data ?? { success: false, message: r.ok ? 'Invalid response format.' : (body && typeof body === 'string' && body.length < 500 ? body : `Upload failed (${r.status}). Server may have returned an error page.`) } };
            })
            .then((d) => {
              const payload = d?.data || {};
              setHsdUploading(false);
              if (d.ok && payload.success) {
                setLastHsdResult({
                  updated: payload.updated ?? 0,
                  timeDistanceUpdated: payload.timeDistanceUpdated ?? payload.tdPromotedCount ?? 0,
                  countiesUpserted: payload.countiesUpserted ?? 0
                });
                setLastHsdFilename(filename);
                const hasWarnings = (payload.warningsSummary?.total ?? 0) > 0;
                const parts = [`${payload.updated ?? 0} minimums`, payload.timeDistanceUpdated != null ? `${payload.timeDistanceUpdated} time/distance` : null, payload.countiesUpserted != null ? `${payload.countiesUpserted} counties` : null].filter(Boolean);
                setMessage(hasWarnings
                  ? `HSD ingest succeeded with ${payload.warningsSummary.total} warning(s). ${parts.join(', ')}. Review upload findings below.`
                  : `HSD ingest complete: ${parts.join(', ')}. Reference section below updated.`);
                setIngestionSummary({
                  success: true,
                  status: hasWarnings ? 'success_with_warnings' : 'success',
                  batch_id: payload.batch_id ?? null,
                  timestamp: payload.uploaded_at ?? payload.timestamp ?? null,
                  count_rows: 0,
                  td_rows: 0,
                  validationFailures: [],
                  warningsSummary: payload.warningsSummary ?? null,
                  warningCounts: payload.warningCounts ?? null,
                  warningsSample: payload.warningsSample ?? null
                });
                setHsdFile(null);
                if (e.target?.querySelector?.('input[type="file"]')) e.target.querySelector('input[type="file"]').value = '';
                onRefresh();
                fetchCountyRef();
                fetchRawIngestion();
                setTimeout(() => { onRefresh(); fetchCountyRef(); fetchRawIngestion(); }, 500);
              } else {
                setIngestionSummary({
                  success: false,
                  status: 'failure',
                  batch_id: payload.batch_id ?? null,
                  timestamp: payload.uploaded_at ?? payload.timestamp ?? null,
                  count_rows: ingestionCountRows.length,
                  td_rows: ingestionTDRows.length,
                  validationFailures: Array.isArray(payload.validationFailures) ? payload.validationFailures : []
                });
                setMessage(payload.message || 'Upload failed');
              }
            })
            .catch((err) => { setHsdUploading(false); setMessage(err.message || 'Upload failed'); });
        }} className="flex flex-wrap items-end gap-3 mb-4">
          <input type="file" accept=".xlsx,.xls" onChange={(e) => setHsdFile(e.target.files?.[0] || null)} className="text-sm text-slate-600 file:mr-2 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-emerald-100 file:text-emerald-800 file:font-medium" />
          <button type="submit" disabled={!hsdFile || hsdUploading} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-xl text-sm font-bold">
            {hsdUploading ? 'Ingesting…' : 'Ingest HSD Reference'}
          </button>
        </form>

        <h3 className="text-base font-bold text-slate-800 mb-2 mt-6">Other sources (PDF / AI)</h3>
        <p className="text-slate-600 text-sm mb-4">
          <strong>Upload the official CMS PDF:</strong> &quot;Medicare Advantage and Section 1876 Cost Plan Network Adequacy Guidance&quot; (current contract year from cms.gov). We extract the minimum-number-of-providers and minimum-number-of-facilities tables from it and update the reference table. You can also upload an Excel or CSV with columns: specialty_code, result_type, county_type, min_count.
        </p>
        <div className="flex flex-wrap items-end gap-4 mb-4">
          <a href={`${apiBase}/settings/cms-minimums/template`} download className="text-sm text-violet-600 hover:underline font-medium">Download Excel/CSV template</a>
          <form onSubmit={handleUpload} className="flex flex-wrap items-end gap-3">
            <input type="file" accept=".pdf,.xlsx,.xls,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm text-slate-600 file:mr-2 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-violet-100 file:text-violet-800 file:font-medium" />
            <button type="submit" disabled={!file || uploading} className="px-4 py-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white rounded-xl text-sm font-bold">
              {uploading ? 'Uploading…' : 'Upload (PDF or Excel)'}
            </button>
          </form>
        </div>
        {message && (
          <div className={`px-4 py-2 rounded-lg text-sm ${message.startsWith('Updated') || message.startsWith('Applied') || message.startsWith('DB updated') || message.startsWith('HSD ingest') ? 'bg-emerald-100 text-emerald-800' : message.startsWith('Extracted') ? 'bg-blue-50 text-blue-800' : 'bg-amber-100 text-amber-800'}`}>{message}</div>
        )}

        <div className="mt-4 p-3 rounded-lg border border-slate-200 bg-slate-50">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-bold text-slate-700">Ingestion Summary</h4>
            <button type="button" onClick={fetchRawIngestion} className="text-xs text-violet-700 hover:underline">Refresh raw ingestion</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-slate-700">
            <div><strong>Status:</strong> {ingestionSummary?.status === 'success_with_warnings' ? 'Success with warnings' : (ingestionSummary?.status || (ingestionSummary?.success === false ? 'failure' : 'success'))}</div>
            <div><strong>Batch ID:</strong> {ingestionSummary?.batch_id || '—'}</div>
            <div><strong>Timestamp:</strong> {ingestionSummary?.timestamp ? new Date(ingestionSummary.timestamp).toLocaleString() : '—'}</div>
            <div><strong>Count rows:</strong> {ingestionCountRows.length}</div>
            <div><strong>TD rows:</strong> {ingestionTDRows.length}</div>
            <div><strong>Total rows:</strong> {ingestionCountRows.length + ingestionTDRows.length}</div>
          </div>
          {ingestionSummary?.success && (ingestionSummary?.warningsSummary?.total ?? 0) > 0 && (
            <div className="mt-3 p-3 rounded-lg border border-amber-200 bg-amber-50">
              <p className="text-sm text-amber-900 font-medium">{ingestionSummary.warningsSummary?.message ?? `${ingestionSummary.warningsSummary?.total} warning(s) recorded.`}</p>
              {ingestionSummary.warningCounts && Object.keys(ingestionSummary.warningCounts).length > 0 && (
                <p className="text-xs text-amber-800 mt-1">
                  {Object.entries(ingestionSummary.warningCounts).map(([type, count]) => `${type.replace(/_/g, ' ')}: ${count}`).join(' · ')}
                </p>
              )}
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (!ingestionSummary?.batch_id || hsdWarningsLoading) return;
                    setHsdWarningsExpanded(!hsdWarningsExpanded);
                    if (!hsdWarningsExpanded && !hsdWarningsDetail) {
                      setHsdWarningsLoading(true);
                      fetch(`${apiBase}/ingestion/batches/${ingestionSummary.batch_id}/warnings`, { credentials: 'include' })
                        .then((r) => r.json())
                        .then((d) => {
                          setHsdWarningsDetail(d.success ? d : null);
                          setHsdWarningsLoading(false);
                        })
                        .catch(() => setHsdWarningsLoading(false));
                    }
                  }}
                  className="text-sm text-amber-800 hover:text-amber-900 font-medium underline"
                >
                  {hsdWarningsLoading ? 'Loading…' : hsdWarningsExpanded ? 'Hide upload warnings' : 'View upload warnings'}
                </button>
              </div>
              {hsdWarningsExpanded && hsdWarningsDetail?.warnings && (
                <div className="mt-3 overflow-x-auto border border-amber-200 rounded bg-white max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-amber-100 sticky top-0">
                      <tr className="text-left text-amber-900">
                        <th className="p-2 font-semibold">Type</th>
                        <th className="p-2 font-semibold">result_type</th>
                        <th className="p-2 font-semibold">specialty_code</th>
                        <th className="p-2 font-semibold">county_type</th>
                        <th className="p-2 font-semibold">message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hsdWarningsDetail.warnings.slice(0, 100).map((w, idx) => (
                        <tr key={w.id || idx} className="border-t border-amber-100">
                          <td className="p-2">{w.finding_type ?? '—'}</td>
                          <td className="p-2">{w.result_type ?? '—'}</td>
                          <td className="p-2">{w.specialty_code ?? '—'}</td>
                          <td className="p-2">{w.county_type ?? '—'}</td>
                          <td className="p-2 max-w-xs truncate" title={w.message}>{w.message ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {hsdWarningsDetail.warnings.length > 100 && (
                    <p className="text-xs text-slate-500 p-2 border-t">Showing first 100 of {hsdWarningsDetail.warnings.length} warnings.</p>
                  )}
                </div>
              )}
            </div>
          )}
          {ingestionSummary?.success === false && Array.isArray(ingestionSummary?.validationFailures) && ingestionSummary.validationFailures.length > 0 && (
            <div className="mt-3 overflow-x-auto border border-amber-200 rounded-lg bg-white">
              <table className="w-full text-xs">
                <thead className="bg-amber-50">
                  <tr className="text-left text-amber-900">
                    <th className="p-2 font-semibold">failure_type</th>
                    <th className="p-2 font-semibold">result_type</th>
                    <th className="p-2 font-semibold">specialty_code</th>
                    <th className="p-2 font-semibold">county_type</th>
                    <th className="p-2 font-semibold">message_for_user</th>
                  </tr>
                </thead>
                <tbody>
                  {ingestionSummary.validationFailures.map((f, idx) => (
                    <tr key={`${idx}-${f.failure_type || 'failure'}`} className="border-t border-amber-100">
                      <td className="p-2">{f.failure_type || '—'}</td>
                      <td className="p-2">{f.result_type || '—'}</td>
                      <td className="p-2">{f.specialty_code || '—'}</td>
                      <td className="p-2">{f.county_type || '—'}</td>
                      <td className="p-2">{f.message_for_user || f.message || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow border border-slate-200 p-5">
        <h3 className="text-base font-bold text-slate-800 mb-1">Census / Population Data</h3>
        <p className="text-slate-600 text-sm mb-3">
          For <strong>population-weighted</strong> coverage (CMS expects % of beneficiaries within standard), load population by ZIP.
          ZIP centroids must exist first; this updates <code className="bg-slate-100 px-1 rounded">population_weight</code> only.
        </p>

        {/* Status badge */}
        {populationWeightedZipCount !== null && (
          <div className={`inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full mb-4 ${
            populationWeightedZipCount > 0
              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              : 'bg-amber-50 text-amber-700 border border-amber-200'
          }`}>
            {populationWeightedZipCount > 0
              ? `${populationWeightedZipCount.toLocaleString()} ZIP centroids have Census population weights — scans will use population-weighted coverage.`
              : 'No Census population weights loaded — scans will use uniform (equal) weighting.'}
          </div>
        )}

        {/* One-click Census load */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 mb-0.5">Load Census 2020 ZCTA Population (One Click)</p>
              <p className="text-xs text-slate-500">
                Pulls all ~33,000 ZCTA populations directly from the U.S. Census Bureau API and updates matching ZIP centroids.
                No file needed. Requires ZIP centroids to be loaded first.
              </p>
            </div>
            <button
              type="button"
              disabled={censusLoading}
              onClick={() => {
                setCensusLoading(true);
                setCensusResult(null);
                fetch(`${apiBase}/census/load-zcta-population`, { method: 'POST', credentials: 'include' })
                  .then((r) => r.json())
                  .then((d) => {
                    setCensusResult(d);
                    if (d.success && onRefreshReadiness) onRefreshReadiness();
                  })
                  .catch((err) => setCensusResult({ success: false, message: err.message }))
                  .finally(() => setCensusLoading(false));
              }}
              className="shrink-0 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white rounded-lg text-sm font-medium transition-colors"
            >
              {censusLoading ? 'Loading Census data…' : 'Load Census 2020 Population'}
            </button>
          </div>
          {censusResult && (
            <div className={`mt-3 text-xs rounded-lg px-3 py-2 ${censusResult.success ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
              {censusResult.success
                ? <>
                    <strong>{censusResult.updated?.toLocaleString()} ZIP centroids updated</strong> with 2020 Census population weights.{' '}
                    {censusResult.not_matched > 0 && <span>{censusResult.not_matched.toLocaleString()} ZCTAs had no matching ZIP centroid (normal — not all ZCTAs map to your service area).{' '}</span>}
                    Scans will now use <strong>population-weighted coverage</strong>.
                  </>
                : censusResult.message}
            </div>
          )}
        </div>

        {/* Manual CSV upload (fallback) */}
        <details className="group">
          <summary className="cursor-pointer text-sm text-slate-500 hover:text-slate-700 font-medium select-none list-none flex items-center gap-1">
            Manual CSV upload (advanced)
          </summary>
          <div className="mt-3">
            <p className="text-xs text-slate-500 mb-2">
              Use Census ZCTA data from <a href="https://data.census.gov" target="_blank" rel="noreferrer" className="text-violet-600 hover:underline">data.census.gov</a>. CSV columns: <code className="bg-slate-100 px-1 rounded">zip_code</code>, <code className="bg-slate-100 px-1 rounded">population</code>.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const file = e.target.querySelector('input[type="file"]')?.files?.[0];
                const type = e.target.querySelector('select')?.value || 'zip_population';
                if (!file) { setMessage('Select a CSV file.'); return; }
                const fd = new FormData();
                fd.append('file', file);
                fd.append('type', type);
                setMessage(null);
                fetch(`${apiBase}/access-points/load`, { method: 'POST', credentials: 'include', body: fd })
                  .then((r) => r.json())
                  .then((d) => {
                    if (d.success) {
                      setMessage(`ZIP population: ${d.updated ?? d.loaded ?? 0} updated.`);
                      if (onRefreshReadiness) onRefreshReadiness();
                    } else setMessage(d.message || 'Upload failed');
                  })
                  .catch((err) => setMessage(err.message || 'Upload failed'));
              }}
              className="flex flex-wrap items-end gap-3"
            >
              <select className="rounded border border-slate-300 px-3 py-2 text-sm" defaultValue="zip_population">
                <option value="zip_population">ZIP population only (zip_code, population)</option>
                <option value="zip_centroids">ZIP centroids full (zip_code, ssa_state_county_code, lat, lon, population_weight)</option>
              </select>
              <input type="file" accept=".csv" className="text-sm file:mr-2 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-violet-100 file:text-violet-800" />
              <button type="submit" className="px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white rounded-lg text-sm font-medium">Upload</button>
            </form>
          </div>
        </details>
      </div>

      <div className="bg-white rounded-xl shadow border border-slate-200 p-5">
        <h3 className="text-base font-bold text-slate-800 mb-2">Membership Enrollment (Members at Risk)</h3>
        <p className="text-slate-600 text-sm mb-3">
          Upload member counts by geography for <strong>Members at Risk</strong> prioritization. CSV: <code className="bg-slate-100 px-1 rounded">zip_code</code> or <code className="bg-slate-100 px-1 rounded">ssa_state_county_code</code>, <code className="bg-slate-100 px-1 rounded">member_count</code>, optional <code className="bg-slate-100 px-1 rounded">contract_id</code>, <code className="bg-slate-100 px-1 rounded">plan_id</code>, <code className="bg-slate-100 px-1 rounded">pbp_id</code>, <code className="bg-slate-100 px-1 rounded">snapshot_date</code>. If no enrollment data, census population weight is used as fallback.
        </p>
        <a
          href="data:text/csv;charset=utf-8,zip_code%2Cssa_state_county_code%2Cmember_count%2Ccontract_id%2Cplan_id%2Cpbp_id%2Csnapshot_date%0A12345%2C%2C150%2C%2C%2C%2C2025-01-01%0A%2C12095%2C200%2C%2C%2C%2C2025-01-01"
          download="membership_enrollment_template.csv"
          className="text-sm text-violet-600 hover:text-violet-700 font-medium mb-2 inline-block"
        >
          Download sample CSV template
        </a>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const file = e.target.querySelector('input[type="file"]')?.files?.[0];
            const recompute = e.target.querySelector('input[type="checkbox"]')?.checked ?? false;
            if (!file) { setMessage('Select a CSV file.'); return; }
            const fd = new FormData();
            fd.append('file', file);
            if (recompute) fd.append('recompute', 'true');
            setMessage(null);
            fetch(`${apiBase}/membership/upload`, { method: 'POST', credentials: 'include', body: fd })
              .then((r) => r.json())
              .then((d) => {
                if (d.success) setMessage(`Membership: ${d.imported} rows imported.${d.centroidsUpdated ? ` ${d.centroidsUpdated} county location(s) resolved for map.` : ''}${d.errors?.length ? ` ${d.errors.length} error(s).` : ''}${recompute ? ' Members-at-risk recomputed for latest scans.' : ''}`);
                else setMessage(d.message || 'Upload failed');
              })
              .catch((err) => setMessage(err.message || 'Upload failed'));
          }}
          className="flex flex-wrap items-end gap-3"
        >
          <input type="file" accept=".csv" className="text-sm file:mr-2 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-violet-100 file:text-violet-800" required />
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" name="recompute" className="rounded" />
            Recompute members-at-risk after import
          </label>
          <button type="submit" className="px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white rounded-lg text-sm font-medium">Upload</button>
        </form>
      </div>

      <div className="bg-white rounded-xl shadow border border-slate-200 p-5">
        <h3 className="text-base font-bold text-slate-800 mb-2">CON Credit (42 CFR 422.116(d)(6))</h3>
        <p className="text-slate-600 text-sm mb-3">
          States with Certificate of Need or anti-competitive restrictions receive a 10 percentage point credit toward TD standard. Add 2-digit FIPS state codes (e.g. 01=Alabama, 12=Florida).
        </p>
        {conStatesLoading ? (
          <p className="text-slate-500 text-sm">Loading…</p>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-2 mb-3">
              <input
                type="text"
                placeholder="State FIPS (e.g. 01)"
                value={conStateFips}
                onChange={(e) => setConStateFips(e.target.value.replace(/\D/g, '').slice(0, 2))}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm w-24"
              />
              <button
                type="button"
                disabled={conStateFips.length < 2 || conStateSaving}
                onClick={() => {
                  const fips = conStateFips.padStart(2, '0');
                  const existing = conStates.find((s) => s.state_fips === fips);
                  const next = existing
                    ? conStates.map((s) => (s.state_fips === fips ? { ...s, con_credit: !s.con_credit } : s))
                    : [...conStates, { state_fips: fips, state_name: null, con_credit: true }];
                  setConStateSaving(true);
                  fetch(`${apiBase}/settings/con-states`, {
                    method: 'PUT',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ states: next })
                  })
                    .then((r) => r.json())
                    .then((d) => {
                      if (d.success) {
                        setConStates(d.states);
                        setConStateFips('');
                      }
                      setConStateSaving(false);
                    })
                    .catch(() => setConStateSaving(false));
                }}
                className="px-4 py-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
              >
                {conStateSaving ? 'Saving…' : (conStates.find((s) => s.state_fips === conStateFips.padStart(2, '0')) ? 'Toggle' : 'Add')}
              </button>
            </div>
            {conStates.length > 0 ? (
              <ul className="text-sm text-slate-700 space-y-1">
                {conStates.filter((s) => s.con_credit).map((s) => (
                  <li key={s.state_fips} className="flex items-center gap-2">
                    <span className="font-mono">{s.state_fips}</span>
                    {s.state_name && <span className="text-slate-500">— {s.state_name}</span>}
                    <span className="text-emerald-600 font-medium">+10 pts</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-500 text-sm">No CON states configured. Add state FIPS codes above.</p>
            )}
          </>
        )}
      </div>

      <div className="bg-white rounded-xl shadow border border-slate-200 p-5">
        <h3 className="text-base font-bold text-slate-800 mb-2">Or paste/upload the minimums table (AI)</h3>
        <p className="text-slate-600 text-sm mb-3">
          Paste the table from the CMS guidance (or upload CSV/Excel). AI will extract specialty code, name, type, county type, and minimum count. Review the result and click Apply to use it.
        </p>
        <div className="flex flex-col gap-3 mb-3">
          <textarea value={tableText} onChange={(e) => setTableText(e.target.value)} placeholder="Paste the minimums table here…" className="w-full h-32 rounded-lg border border-slate-200 p-2 text-sm font-mono" />
          <div className="flex flex-wrap items-center gap-3">
            <input type="file" accept=".pdf,.csv,.txt,.xlsx,.xls" onChange={(e) => setTableFile(e.target.files?.[0] || null)} className="text-sm text-slate-600 file:mr-2 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-indigo-800" />
            <button type="button" onClick={handleAnalyzeTable} disabled={analyzing || (!tableText.trim() && !tableFile)} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl text-sm font-bold inline-flex items-center gap-2">
              {analyzing && (
                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden />
              )}
              {analyzing ? 'Analyzing…' : 'Analyze with AI'}
            </button>
          </div>
        </div>
        {analyzing && (
          <div className="flex items-center gap-3 p-4 rounded-xl bg-indigo-50 border border-indigo-100 mb-3 animate-pulse">
            <span className="inline-block w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" aria-hidden />
            <div>
              <p className="text-sm font-medium text-indigo-800">AI is analyzing your document</p>
              <p className="text-xs text-indigo-600 mt-0.5">Extracting provider and facility tables… this may take 10–30 seconds.</p>
            </div>
            <div className="flex gap-1 ml-2">
              <span className="w-2 h-2 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 rounded-full bg-indigo-500 animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 rounded-full bg-indigo-600 animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}
        {analyzedRows && analyzedRows.length > 0 && (
          <>
            <p className="text-slate-600 text-sm mb-2">Preview: {(() => {
              const byKey = new Map();
              analyzedRows.forEach((r) => {
                const key = `${r.specialty_code}\t${r.specialty_name ?? ''}\t${r.result_type}\t${r.min_count}`;
                if (!byKey.has(key)) byKey.set(key, { ...r, countyTypes: [] });
                byKey.get(key).countyTypes.push(r.county_type);
              });
              const grouped = [...byKey.values()];
              return <>{grouped.length} specialties ({analyzedRows.length} rows for DB). Confirm then Apply.</>;
            })()}</p>
            <div className="overflow-x-auto max-h-48 overflow-y-auto border border-slate-200 rounded-lg mb-3">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left text-slate-600">
                    <th className="p-2 font-semibold">Code</th>
                    <th className="p-2 font-semibold">Name</th>
                    <th className="p-2 font-semibold">Type</th>
                    <th className="p-2 font-semibold">Min</th>
                    <th className="p-2 font-semibold">County types</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const byKey = new Map();
                    analyzedRows.forEach((r) => {
                      const key = `${r.specialty_code}\t${r.specialty_name ?? ''}\t${r.result_type}\t${r.min_count}`;
                      if (!byKey.has(key)) byKey.set(key, { ...r, countyTypes: [] });
                      byKey.get(key).countyTypes.push(r.county_type);
                    });
                    return [...byKey.values()].slice(0, 80).map((g, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="p-2">{g.specialty_code}</td>
                        <td className="p-2">{g.specialty_name ?? '—'}</td>
                        <td className="p-2">{g.result_type}</td>
                        <td className="p-2 font-medium">{g.min_count}</td>
                        <td className="p-2 text-slate-500">{[...new Set(g.countyTypes)].sort().join(', ')}</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
            {(() => {
              const byKey = new Map();
              analyzedRows.forEach((r) => {
                const key = `${r.specialty_code}\t${r.specialty_name ?? ''}\t${r.result_type}\t${r.min_count}`;
                if (!byKey.has(key)) byKey.set(key, true);
              });
              return byKey.size > 80 ? <p className="text-slate-500 text-xs mb-2">Showing first 80 specialties.</p> : null;
            })()}
            <button type="button" onClick={handleApplyAnalyzed} disabled={applyLoading} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-xl text-sm font-bold">
              {applyLoading ? 'Applying…' : 'Apply to current minimums'}
            </button>
          </>
        )}
      </div>

      <div className="bg-white rounded-xl shadow border border-slate-200 p-5">
        <h3 className="text-base font-bold text-slate-800 mb-2">Raw Ingestion</h3>
        <p className="text-slate-600 text-xs mb-3">
          1 row = 1 DB record. No grouping, no aggregation.
        </p>
        {ingestionLoading ? (
          <p className="text-slate-500 text-sm mb-4">Loading raw ingestion…</p>
        ) : (
          <>
            <h4 className="text-sm font-bold text-slate-700 mb-1">Table 1: Count Minimums ({ingestionCountRows.length})</h4>
            <div className="overflow-x-auto max-h-64 overflow-y-auto border border-slate-200 rounded-lg mb-4">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left text-slate-600">
                    <th className="p-2 font-semibold">specialty_code</th>
                    <th className="p-2 font-semibold">result_type</th>
                    <th className="p-2 font-semibold">county_type</th>
                    <th className="p-2 font-semibold">min_count</th>
                    <th className="p-2 font-semibold">source</th>
                    <th className="p-2 font-semibold">batch_id</th>
                    <th className="p-2 font-semibold">uploaded_at</th>
                  </tr>
                </thead>
                <tbody>
                  {ingestionCountRows.map((r, idx) => (
                    <tr key={`count-${idx}-${r.specialty_code || ''}-${r.result_type || ''}-${r.county_type || ''}`} className="border-t border-slate-100">
                      <td className="p-2">{r.specialty_code || '—'}</td>
                      <td className="p-2">{r.result_type || '—'}</td>
                      <td className="p-2">{r.county_type || '—'}</td>
                      <td className="p-2">{r.min_count ?? '—'}</td>
                      <td className="p-2">{r.source || '—'}</td>
                      <td className="p-2 font-mono text-xs">{r.batch_id || 'null'}</td>
                      <td className="p-2 text-xs">{r.uploaded_at ? new Date(r.uploaded_at).toLocaleString() : 'null'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h4 className="text-sm font-bold text-slate-700 mb-1">Table 2: Time &amp; Distance Standards ({ingestionTDRows.length})</h4>
            <div className="overflow-x-auto max-h-64 overflow-y-auto border border-slate-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left text-slate-600">
                    <th className="p-2 font-semibold">specialty_code</th>
                    <th className="p-2 font-semibold">result_type</th>
                    <th className="p-2 font-semibold">county_type</th>
                    <th className="p-2 font-semibold">max_time_minutes</th>
                    <th className="p-2 font-semibold">max_distance_miles</th>
                    <th className="p-2 font-semibold">source</th>
                    <th className="p-2 font-semibold">batch_id</th>
                    <th className="p-2 font-semibold">uploaded_at</th>
                  </tr>
                </thead>
                <tbody>
                  {ingestionTDRows.map((r, idx) => (
                    <tr key={`td-${idx}-${r.specialty_code || ''}-${r.result_type || ''}-${r.county_type || ''}`} className="border-t border-slate-100">
                      <td className="p-2">{r.specialty_code || '—'}</td>
                      <td className="p-2">{r.result_type || '—'}</td>
                      <td className="p-2">{r.county_type || '—'}</td>
                      <td className="p-2">{r.max_time_minutes ?? '—'}</td>
                      <td className="p-2">{r.max_distance_miles ?? '—'}</td>
                      <td className="p-2">{r.source || '—'}</td>
                      <td className="p-2 font-mono text-xs">{r.batch_id || 'null'}</td>
                      <td className="p-2 text-xs">{r.uploaded_at ? new Date(r.uploaded_at).toLocaleString() : 'null'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div className="bg-white rounded-xl shadow border border-slate-200 p-5">
        <h3 className="text-base font-bold text-slate-800 mb-2">Reference data in use for adequacy analysis</h3>
        <p className="text-slate-600 text-xs mb-2">
          The following is stored in the DB and used by the scan engine. All of it is updated when you ingest the HSD reference file.
        </p>
        <p className="text-slate-600 text-xs mb-3 font-medium">
          This universe includes: <strong>(1) County reference</strong> and <strong>(2) Minimums and time/distance</strong>.
        </p>

        {lastHsdResult && (
          <div className="mb-4 p-3 rounded-lg border-2 border-emerald-200 bg-emerald-50 text-sm">
            <p className="font-bold text-emerald-800 mb-2">Last HSD ingest</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-slate-700">
              <span><strong>Minimums:</strong> {lastHsdResult.updated ?? 0} rows</span>
              <span><strong>Time/distance:</strong> {lastHsdResult.timeDistanceUpdated ?? 0} rows</span>
              <span><strong>Counties:</strong> {lastHsdResult.countiesUpserted ?? 0} rows</span>
              <span><strong>File:</strong> {lastHsdFilename || '—'}</span>
            </div>
          </div>
        )}

        <h4 className="text-sm font-bold text-slate-700 mb-1 mt-4">1. County reference</h4>
        <p className="text-slate-600 text-xs mb-2">
          Used by the scan engine to resolve county type (e.g. metro, rural) per county so requirements match the correct geography.
        </p>
        {(lastHsdResult?.countiesUpserted != null || countyRefCount != null) ? (
          <div className="p-3 rounded-lg border border-slate-200 bg-slate-50 text-sm mb-4">
            {lastHsdResult?.countiesUpserted != null && (
              <div className="space-y-1 mb-2">
                <p className="text-emerald-700 font-medium">HSD county reference loaded successfully</p>
                <p className="text-slate-700"><strong>Counties upserted:</strong> {lastHsdResult.countiesUpserted}</p>
                <p className="text-slate-700"><strong>Source:</strong> HSD reference file</p>
                <p className="text-slate-700"><strong>Version:</strong> {lastHsdFilename || '—'}</p>
              </div>
            )}
            {countyRefCount != null && (
              <p className="text-slate-700">
                County reference in DB: <strong>{countyRefCount}</strong> total{countyRefFromHsd != null && countyRefFromHsd !== countyRefCount ? ` (<strong>{countyRefFromHsd}</strong> from HSD reference file)` : countyRefFromHsd != null ? ' (all from HSD reference file)' : ''}.
              </p>
            )}
          </div>
        ) : (
          <p className="text-slate-500 text-sm mb-4">No county reference loaded. Ingest the HSD reference file above to load county reference.</p>
        )}

        <h4 className="text-sm font-bold text-slate-700 mb-1 mt-4">2. Minimums and time/distance</h4>
        <p className="text-slate-600 text-xs mb-2">
          <strong>Specialty</strong> = CMS code + name (e.g. 040 = Acute Inpatient Hospitals). <strong>County type</strong> = CMS geography: large_metro, metro, micro, rural, ceac (CEAC = County Equivalent Area).
        </p>
        {loading ? (
          <p className="text-slate-500">Loading…</p>
        ) : (
          <>
            <p className="text-slate-600 text-sm mb-3">
              Total: <strong>{summary.total}</strong> row(s) in DB. By source: {Object.entries(summary.by_source || {}).map(([k, v]) => `${k}: ${v}`).join(', ') || '—'}
              {lastHsdResult?.updated != null && (
                <span className="ml-2 text-emerald-700 font-medium">· Last HSD: {lastHsdResult.updated} minimums, {lastHsdResult.timeDistanceUpdated ?? 0} time/distance</span>
              )}
            </p>
            {requirements.length > 0 ? (
              <div className="overflow-x-auto max-h-64 overflow-y-auto border border-slate-100 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="text-left text-slate-600">
                      <th className="p-2 font-semibold">Specialty</th>
                      <th className="p-2 font-semibold">Type</th>
                      <th className="p-2 font-semibold">Min</th>
                      <th className="p-2 font-semibold">County types</th>
                      {(requirements.some((r) => r.max_time_minutes != null)) && <th className="p-2 font-semibold">Max time (min)</th>}
                      {(requirements.some((r) => r.max_distance_miles != null)) && <th className="p-2 font-semibold">Max dist (mi)</th>}
                      <th className="p-2 font-semibold">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const byKey = new Map();
                      requirements.forEach((r) => {
                        const key = `${r.specialty_code}\t${r.result_type}\t${r.min_count}\t${r.cms_source || ''}`;
                        if (!byKey.has(key)) byKey.set(key, { ...r, countyTypes: [] });
                        byKey.get(key).countyTypes.push(r.county_type);
                      });
                      return [...byKey.values()].slice(0, 100).map((g, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="p-2 text-slate-800" title={g.specialty_code}>
                            {g.specialty_display_name ? `${g.specialty_code} – ${g.specialty_display_name}` : g.specialty_code}
                          </td>
                          <td className="p-2 text-slate-600">{g.result_type}</td>
                          <td className="p-2 font-medium">{g.min_count}</td>
                          <td className="p-2 text-slate-500 text-xs">{[...new Set(g.countyTypes)].sort().join(', ')}</td>
                          {(requirements.some((x) => x.max_time_minutes != null)) && <td className="p-2 text-slate-600">{g.max_time_minutes ?? '—'}</td>}
                          {(requirements.some((x) => x.max_distance_miles != null)) && <td className="p-2 text-slate-600">{g.max_distance_miles ?? '—'}</td>}
                          <td className="p-2 text-slate-500 text-xs max-w-[200px] truncate" title={g.cms_source}>{g.cms_source || '—'}</td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-slate-500">No minimums loaded. Upload the CMS PDF above or use AI to analyze and Apply.</p>
            )}
            {requirements.length > 100 && <p className="text-slate-500 text-xs mt-2">Showing first 100 specialties (grouped).</p>}
          </>
        )}
      </div>
    </div>
  );
}
